import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getBlob, ref, uploadBytesResumable } from "firebase/storage";
import { NavLink, Navigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  AtSign,
  ArrowLeft,
  BarChart3,
  Beaker,
  Bell,
  Bot,
  BookOpen,
  Bug,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  ClipboardCheck,
  Clock3,
  Code2,
  Compass,
  Eye,
  FilePenLine,
  Flag,
  Layers3,
  LayoutGrid,
  Link2,
  ListChecks,
  Map,
  Megaphone,
  MessageSquare,
  Mic,
  Paperclip,
  Plus,
  Rocket,
  Reply,
  Save,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  StopCircle,
  Tag,
  Target,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { useAuth } from "./auth";
import { db, functions, storage } from "./firebase";
import { ProjectFiles } from "./workspace-files";
import type {
  DraftPuzzle,
  GameBlueprint,
  GameProject,
  ProductionStageId,
  StudioIssue,
  StudioIssueSeverity,
  StudioIssueStatus,
  StudioConversation,
  StudioConversationType,
  StudioMessage,
  StudioMessageAttachment,
  StudioNotification,
  StudioNotificationPreferences,
  StudioPresence,
  StudioAiAction,
  StudioAiConversation,
  StudioAiMessage,
  StudioPermission,
  StudioRole,
  StudioSprint,
  StudioSprintStatus,
  StudioTask,
  StudioTaskPriority,
  StudioTaskStatus,
  StudioTaskType,
  WorkspaceItem,
  WorkspaceItemStatus,
  WorkspaceItemType,
} from "./types";

const stages: Array<{
  id: ProductionStageId;
  label: string;
  description: string;
  deliverables: string[];
}> = [
  {
    id: "brief",
    label: "1. Concept",
    description: "Vision, public, promesse et objectifs.",
    deliverables: [
      "Promesse joueur",
      "Public cible",
      "Objectifs mesurables",
      "Portée approuvée",
    ],
  },
  {
    id: "narrative",
    label: "2. Scénario",
    description: "Univers, histoire, personnages et conclusion.",
    deliverables: [
      "Synopsis",
      "Ouverture et conclusion",
      "Personnages",
      "Règles de l’univers",
    ],
  },
  {
    id: "route",
    label: "3. Parcours",
    description: "Lieux, déplacements, sécurité et accessibilité.",
    deliverables: [
      "Départ et arrivée",
      "Distance et durée",
      "Plan de sécurité",
      "Solution météo",
    ],
  },
  {
    id: "puzzles",
    label: "4. Énigmes",
    description: "Questions, réponses, indices et progression.",
    deliverables: [
      "Courbe de difficulté",
      "Énigmes complètes",
      "Indices gradués",
      "Solutions vérifiées",
    ],
  },
  {
    id: "development",
    label: "5. Programmation",
    description: "Intégration technique et logique du jeu.",
    deliverables: [
      "Architecture",
      "Intégrations",
      "Mesures analytiques",
      "Plan hors ligne",
    ],
  },
  {
    id: "qa",
    label: "6. Contrôle qualité",
    description: "Révision, cohérence et tests techniques.",
    deliverables: [
      "Tests mobiles",
      "Tests terrain",
      "Anomalies traitées",
      "Régression finale",
    ],
  },
  {
    id: "sandbox",
    label: "7. Sandbox",
    description: "Essai complet réservé à l’équipe.",
    deliverables: [
      "Parcours intégral",
      "Temps réel mesuré",
      "Commentaires testeurs",
      "Approbation finale",
    ],
  },
  {
    id: "launch",
    label: "8. Finalisation",
    description: "Checklist, publication et exploitation.",
    deliverables: [
      "Prix et paiement",
      "Textes légaux",
      "Plan de lancement",
      "Soutien préparé",
    ],
  },
];

const roles: Record<
  StudioRole,
  { label: string; description: string; permissions: StudioPermission[] }
> = {
  producer: {
    label: "Responsable de production",
    description: "Coordonne le projet, les étapes et l’équipe.",
    permissions: [
      "edit_overview",
      "manage_workflow",
      "manage_team",
      "edit_content",
      "test_sandbox",
      "approve_release",
    ],
  },
  writer: {
    label: "Scénariste / concepteur",
    description: "Écrit l’histoire, le parcours et les énigmes.",
    permissions: ["edit_overview", "edit_content", "test_sandbox"],
  },
  developer: {
    label: "Développeur",
    description: "Gère l’intégration et les notes techniques.",
    permissions: ["edit_technical", "edit_content", "test_sandbox"],
  },
  tester: {
    label: "Testeur qualité",
    description: "Teste la sandbox et signale les problèmes.",
    permissions: ["test_sandbox", "report_issues"],
  },
  publisher: {
    label: "Responsable de validation",
    description: "Valide la version finale avant publication admin.",
    permissions: ["test_sandbox", "report_issues", "approve_release"],
  },
};
const permissionLabels: Record<StudioPermission, string> = {
  edit_overview: "Modifier la conception",
  manage_workflow: "Gérer étapes et tâches",
  manage_team: "Gérer l’équipe",
  edit_content: "Créer les énigmes",
  edit_technical: "Architecture technique",
  test_sandbox: "Tester la sandbox",
  report_issues: "Signaler les anomalies",
  approve_release: "Valider la sortie",
};
const taskStatusLabels: Record<StudioTaskStatus, string> = {
  backlog: "Backlog",
  todo: "À faire",
  in_progress: "En cours",
  review: "Révision",
  testing: "Test",
  done: "Terminé",
};
const taskPriorityLabels: Record<StudioTaskPriority, string> = {
  low: "Faible",
  normal: "Normale",
  high: "Haute",
  critical: "Critique",
};
const taskTypeLabels: Record<StudioTaskType, string> = {
  feature: "Fonctionnalité",
  story: "Récit utilisateur",
  task: "Tâche",
  puzzle: "Énigme",
  bug: "Bogue",
  improvement: "Amélioration",
};
const sprintStatusLabels: Record<StudioSprintStatus, string> = {
  planned: "Planifié",
  active: "Actif",
  completed: "Terminé",
};
const issueStatusLabels: Record<StudioIssueStatus, string> = {
  open: "Ouverte",
  in_progress: "En correction",
  resolved: "Corrigée",
  verified: "Vérifiée",
};
const issueSeverityLabels: Record<StudioIssueSeverity, string> = {
  minor: "Mineure",
  major: "Majeure",
  blocker: "Bloquante",
};

const blueprintGroups: Array<{
  id: string;
  title: string;
  icon: typeof Target;
  description: string;
  fields: Array<{
    key: keyof GameBlueprint;
    label: string;
    prompt: string;
    technical?: boolean;
  }>;
}> = [
  {
    id: "vision",
    title: "Vision du jeu",
    icon: Target,
    description: "La raison d’être du projet et l’expérience promise.",
    fields: [
      {
        key: "elevatorPitch",
        label: "Concept en une phrase",
        prompt: "Résumez le jeu en une phrase claire et mémorable.",
      },
      {
        key: "playerPromise",
        label: "Promesse faite au joueur",
        prompt: "Que doit-il ressentir, découvrir ou accomplir?",
      },
      {
        key: "targetAudience",
        label: "Public cible",
        prompt: "Âge, habitudes, taille d’équipe, expérience et contraintes.",
      },
      {
        key: "objectives",
        label: "Objectifs du projet",
        prompt: "Objectifs créatifs, commerciaux et mesurables.",
      },
      {
        key: "themes",
        label: "Thèmes",
        prompt: "Mystères, sujets, lieux et idées centrales.",
      },
      {
        key: "tone",
        label: "Ton et limites",
        prompt: "Ambiance, intensité, humour, peur et contenu à éviter.",
      },
    ],
  },
  {
    id: "story",
    title: "Bible narrative",
    icon: BookOpen,
    description: "La référence officielle de l’histoire.",
    fields: [
      {
        key: "synopsis",
        label: "Synopsis complet",
        prompt: "Début, révélations, progression et résolution.",
      },
      {
        key: "opening",
        label: "Situation de départ",
        prompt: "Comment le joueur entre-t-il dans l’aventure?",
      },
      {
        key: "ending",
        label: "Conclusion",
        prompt: "Révélation finale, récompense et fermeture narrative.",
      },
      {
        key: "characters",
        label: "Personnages",
        prompt: "Rôle, motivation, voix, secrets et relations.",
      },
      {
        key: "loreRules",
        label: "Règles de l’univers",
        prompt: "Ce qui est vrai, impossible ou doit rester cohérent.",
      },
    ],
  },
  {
    id: "route",
    title: "Parcours terrain",
    icon: Map,
    description: "La faisabilité réelle, sécuritaire et accessible.",
    fields: [
      {
        key: "routeStart",
        label: "Point de départ",
        prompt: "Lieu précis, accueil et déclenchement.",
      },
      {
        key: "routeEnd",
        label: "Point d’arrivée",
        prompt: "Lieu final et sortie de l’expérience.",
      },
      {
        key: "distanceKm",
        label: "Distance",
        prompt: "Distance totale et segments critiques.",
      },
      {
        key: "routeDuration",
        label: "Durée terrain",
        prompt: "Marche, arrêts, énigmes et marge.",
      },
      {
        key: "accessibility",
        label: "Accessibilité",
        prompt: "Mobilité, toilettes, transport, obstacles et alternatives.",
      },
      {
        key: "safetyPlan",
        label: "Plan de sécurité",
        prompt: "Circulation, propriétés privées, urgences et consignes.",
      },
      {
        key: "weatherFallback",
        label: "Plan météo",
        prompt: "Pluie, chaleur, froid, fermeture et report.",
      },
      {
        key: "permits",
        label: "Autorisations",
        prompt: "Permissions, partenaires et lieux à confirmer.",
      },
    ],
  },
  {
    id: "experience",
    title: "Expérience de jeu",
    icon: Sparkles,
    description: "La boucle de jeu et la courbe de difficulté.",
    fields: [
      {
        key: "coreLoop",
        label: "Boucle principale",
        prompt: "Observer, discuter, se déplacer, résoudre, progresser.",
      },
      {
        key: "progression",
        label: "Progression",
        prompt: "Rythme, actes, jalons et montée dramatique.",
      },
      {
        key: "difficultyStrategy",
        label: "Difficulté",
        prompt: "Différences entre Histoire, Enquête et Expert.",
      },
      {
        key: "hintStrategy",
        label: "Système d’indices",
        prompt: "Déblocage, pénalités et indices par niveau.",
      },
      {
        key: "failStates",
        label: "Échecs et récupération",
        prompt: "Mauvaise réponse, blocage, abandon et reprise.",
      },
      {
        key: "replayability",
        label: "Rejouabilité",
        prompt: "Variantes, branches ou raisons de recommencer.",
      },
    ],
  },
  {
    id: "direction",
    title: "Direction créative",
    icon: Eye,
    description: "Une expérience visuelle et sonore cohérente.",
    fields: [
      {
        key: "visualDirection",
        label: "Direction visuelle",
        prompt: "Palette, images, typographie, iconographie et références.",
      },
      {
        key: "audioDirection",
        label: "Direction sonore",
        prompt: "Ambiance, musique, voix et accessibilité sonore.",
      },
      {
        key: "uiNotes",
        label: "Interface mobile",
        prompt: "Écrans, navigation, lisibilité extérieure et interactions.",
      },
    ],
  },
  {
    id: "technical",
    title: "Architecture technique",
    icon: Code2,
    description: "Fonctionnement, données et résilience.",
    fields: [
      {
        key: "technicalArchitecture",
        label: "Architecture",
        prompt: "Services, collections, fonctions et logique.",
        technical: true,
      },
      {
        key: "integrations",
        label: "Intégrations",
        prompt: "Paiement, cartes, courriel, géolocalisation et partenaires.",
        technical: true,
      },
      {
        key: "analyticsEvents",
        label: "Mesures",
        prompt: "Événements à suivre sans compromettre la vie privée.",
        technical: true,
      },
      {
        key: "offlinePlan",
        label: "Connexion faible",
        prompt: "Comportement hors ligne, reprise et messages d’erreur.",
        technical: true,
      },
      {
        key: "privacyNotes",
        label: "Données et confidentialité",
        prompt: "Données collectées, conservation, consentement et accès.",
        technical: true,
      },
    ],
  },
  {
    id: "operations",
    title: "Modèle et exploitation",
    icon: BarChart3,
    description: "Prix, lancement, exploitation et succès.",
    fields: [
      {
        key: "businessModel",
        label: "Modèle économique",
        prompt: "Prix, inclusions, rabais et offre entreprise.",
      },
      {
        key: "operatingPlan",
        label: "Exploitation",
        prompt: "Coûts, maintenance, fermetures et responsabilités.",
      },
      {
        key: "partnerships",
        label: "Partenaires",
        prompt: "Lieux, commanditaires, visibilité et engagements.",
      },
      {
        key: "successMetrics",
        label: "Indicateurs de succès",
        prompt: "Ventes, complétion, satisfaction, durée et incidents.",
      },
      {
        key: "launchPlan",
        label: "Plan de lancement",
        prompt: "Bêta, date, communication et capacité initiale.",
      },
      {
        key: "supportPlan",
        label: "Soutien aux joueurs",
        prompt: "Canaux, heures, urgences et remboursement.",
      },
    ],
  },
];
const releaseChecks = [
  ["concept_approved", "Concept et public cible approuvés"],
  ["narrative_locked", "Scénario verrouillé et cohérent"],
  ["route_verified", "Parcours vérifié sur le terrain"],
  ["puzzles_validated", "Toutes les énigmes et réponses validées"],
  ["qa_clear", "Aucune anomalie bloquante ouverte"],
  ["mobile_tested", "Tests sur plusieurs téléphones réussis"],
  ["safety_reviewed", "Sécurité, accessibilité et météo révisées"],
  ["legal_ready", "Conditions et partenaires prêts"],
  ["payment_tested", "Paiement et confirmation testés"],
  ["final_approval", "Approbation finale du responsable"],
] as const;

function hasPermission(
  project: GameProject | undefined,
  uid: string | undefined,
  isAdmin: boolean | undefined,
  permission: StudioPermission,
) {
  return Boolean(
    isAdmin ||
    (uid &&
      project?.members
        ?.find((member) => member.uid === uid)
        ?.permissions?.includes(permission)),
  );
}
function call<T = unknown>(name: string, data: unknown) {
  return httpsCallable<unknown, T>(functions, name)(data);
}
function mentionedMembers(body: string, project: GameProject) {
  const normalized = body.toLocaleLowerCase("fr-CA");
  return project.members
    .filter((member) => {
      const name = (member.displayName || "").toLocaleLowerCase("fr-CA");
      const emailName = member.email.split("@")[0].toLocaleLowerCase("fr-CA");
      return (
        (name && normalized.includes(`@${name}`)) ||
        normalized.includes(`@${emailName}`)
      );
    })
    .map((member) => member.uid);
}

export function GameStudio() {
  const { user, profile } = useAuth();
  const [projects, setProjects] = useState<GameProject[]>([]);
  const [title, setTitle] = useState("");
  const [city, setCity] = useState("Québec");
  const [status, setStatus] = useState("");
  useEffect(() => {
    if (!user) return;
    const base = collection(db, "gameProjects");
    const q = profile?.isAdmin
      ? query(base, orderBy("updatedAt", "desc"))
      : query(base, where("memberIds", "array-contains", user.uid));
    return onSnapshot(q, (s) =>
      setProjects(
        s.docs.map((d) => ({ id: d.id, ...d.data() }) as GameProject),
      ),
    );
  }, [user, profile?.isAdmin]);
  if (!user) return <Navigate to="/" />;
  if (!profile?.isAdmin && !profile?.studioAccess) return <Navigate to="/" />;
  async function create(e: FormEvent) {
    e.preventDefault();
    setStatus("Création…");
    try {
      const result = await call<{ projectId: string }>("createGameProject", {
        title,
        city,
      });
      location.assign(`/atelier/${result.data.projectId}`);
    } catch (error) {
      setStatus(
        error instanceof Error ? error.message : "Création impossible.",
      );
    }
  }
  return (
    <section className="page section studio-home">
      <div className="studio-title">
        <div>
          <span className="eyebrow">
            <Wrench size={16} /> Studio de production
          </span>
          <h1>Atelier de création</h1>
          <p>
            Un système complet pour concevoir, produire, tester et publier
            chaque expérience.
          </p>
        </div>
        {profile.isAdmin && (
          <form className="studio-create" onSubmit={create}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre du nouveau jeu"
              required
            />
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ville"
              required
            />
            <button className="button">
              <Plus /> Nouveau jeu
            </button>
            {status && <small>{status}</small>}
          </form>
        )}
      </div>
      <div className="studio-project-grid">
        {projects.map((project) => {
          const progress = Math.round(
            ((project.completedStages?.length || 0) / stages.length) * 100,
          );
          return (
            <NavLink
              className="studio-project-card"
              to={`/atelier/${project.id}`}
              key={project.id}
            >
              <div className="studio-card-top">
                <span className={`studio-status ${project.status}`}>
                  {project.status}
                </span>
                {project.published && (
                  <span className="published-badge">
                    <Eye size={13} /> Public
                  </span>
                )}
              </div>
              <h2>{project.title}</h2>
              <p>
                <Map size={15} /> {project.city}
              </p>
              <div className="progress">
                <span style={{ width: `${progress}%` }} />
              </div>
              <small>
                {progress}% · {project.completedStages?.length || 0}/
                {stages.length} phases
              </small>
            </NavLink>
          );
        })}
        {projects.length === 0 && (
          <div className="studio-empty">
            <Compass />
            <h2>Aucun projet accessible</h2>
          </div>
        )}
      </div>
      <section className="role-reference">
        <div className="section-heading">
          <span>Accès précis</span>
          <h2>Rôles de production</h2>
        </div>
        <div className="role-grid">
          <article className="role-card admin-role">
            <ShieldCheck />
            <h3>Administrateur</h3>
            <p>Tous les accès et publication finale.</p>
          </article>
          {Object.entries(roles).map(([key, role]) => (
            <article className="role-card" key={key}>
              <Users />
              <h3>{role.label}</h3>
              <p>{role.description}</p>
              <ul>
                {role.permissions.map((p) => (
                  <li key={p}>
                    <Check size={13} />
                    {permissionLabels[p]}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

type StudioTab =
  | "dashboard"
  | "blueprint"
  | "tasks"
  | "workspace"
  | "communication"
  | "assistant"
  | "content"
  | "qa"
  | "team"
  | "sandbox"
  | "release";
export function GameProjectEditor() {
  const { projectId } = useParams();
  const { user, profile } = useAuth();
  const [project, setProject] = useState<GameProject>();
  const [puzzles, setPuzzles] = useState<DraftPuzzle[]>([]);
  const [tasks, setTasks] = useState<StudioTask[]>([]);
  const [issues, setIssues] = useState<StudioIssue[]>([]);
  const [sprints, setSprints] = useState<StudioSprint[]>([]);
  const [workspaceItems, setWorkspaceItems] = useState<WorkspaceItem[]>([]);
  const [notifications, setNotifications] = useState<StudioNotification[]>([]);
  const [tab, setTab] = useState<StudioTab>("dashboard");
  const [notice, setNotice] = useState("");
  useEffect(
    () =>
      projectId
        ? onSnapshot(doc(db, "gameProjects", projectId), (s) =>
            setProject(
              s.exists()
                ? ({ id: s.id, ...s.data() } as GameProject)
                : undefined,
            ),
          )
        : undefined,
    [projectId],
  );
  useEffect(() => {
    if (!user || !projectId) return;
    return onSnapshot(
      query(
        collection(db, "users", user.uid, "studioNotifications"),
        orderBy("createdAt", "desc"),
      ),
      (snapshot) =>
        setNotifications(
          snapshot.docs
            .map((item) => ({
              id: item.id,
              ...item.data(),
            }) as StudioNotification)
            .filter((item) => item.projectId === projectId),
        ),
    );
  }, [user, projectId]);
  useEffect(
    () =>
      projectId
        ? onSnapshot(
            query(
              collection(db, "gameProjects", projectId, "workspace"),
              orderBy("updatedAt", "desc"),
            ),
            (s) =>
              setWorkspaceItems(
                s.docs.map((d) => ({ id: d.id, ...d.data() }) as WorkspaceItem),
              ),
          )
        : undefined,
    [projectId],
  );
  useEffect(
    () =>
      projectId
        ? onSnapshot(
            query(
              collection(db, "gameProjects", projectId, "sprints"),
              orderBy("startDate", "desc"),
            ),
            (s) =>
              setSprints(
                s.docs.map((d) => ({ id: d.id, ...d.data() }) as StudioSprint),
              ),
          )
        : undefined,
    [projectId],
  );
  useEffect(
    () =>
      projectId
        ? onSnapshot(
            query(
              collection(db, "gameProjects", projectId, "draftPuzzles"),
              orderBy("order"),
            ),
            (s) =>
              setPuzzles(
                s.docs.map((d) => ({ id: d.id, ...d.data() }) as DraftPuzzle),
              ),
          )
        : undefined,
    [projectId],
  );
  useEffect(
    () =>
      projectId
        ? onSnapshot(
            query(
              collection(db, "gameProjects", projectId, "tasks"),
              orderBy("updatedAt", "desc"),
            ),
            (s) =>
              setTasks(
                s.docs.map((d) => ({ id: d.id, ...d.data() }) as StudioTask),
              ),
          )
        : undefined,
    [projectId],
  );
  useEffect(
    () =>
      projectId
        ? onSnapshot(
            query(
              collection(db, "gameProjects", projectId, "issues"),
              orderBy("updatedAt", "desc"),
            ),
            (s) =>
              setIssues(
                s.docs.map((d) => ({ id: d.id, ...d.data() }) as StudioIssue),
              ),
          )
        : undefined,
    [projectId],
  );
  if (!user) return <Navigate to="/" />;
  if (!project) return <section className="page section">Chargement…</section>;
  if (!profile?.isAdmin && !project.memberIds?.includes(user.uid))
    return <Navigate to="/" />;
  const can = (p: StudioPermission) =>
    hasPermission(project, user.uid, profile?.isAdmin, p);
  async function toggleStage(stageId: ProductionStageId) {
    if (!project) return;
    const completed = project.completedStages?.includes(stageId);
    try {
      await call("setGameProjectStage", {
        projectId,
        stageId,
        completed: !completed,
      });
      setNotice(completed ? "Phase rouverte." : "Phase terminée.");
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Modification refusée.");
    }
  }
  async function publish() {
    try {
      await call("publishGameProject", { projectId });
      setNotice("Jeu publié.");
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Publication impossible.");
    }
  }
  const openIssues = issues.filter(
    (i) => !["resolved", "verified"].includes(i.status),
  );
  const releaseDone = releaseChecks.filter(
    ([id]) => project.releaseChecks?.[id],
  ).length;
  const unreadNotifications = notifications.filter((item) => !item.read).length;
  const memberRole = project.members?.find((member) => member.uid === user.uid)?.role;
  const canApplyAi = memberRole === "developer";
  return (
    <section className="page section project-editor">
      <NavLink className="studio-back" to="/atelier">
        <ArrowLeft /> Tous les projets
      </NavLink>
      <header className="project-header">
        <div>
          <span className={`studio-status ${project.status}`}>
            {project.status}
          </span>
          <h1>{project.title}</h1>
          <p>
            {project.city} · {puzzles.length} énigmes ·{" "}
            {tasks.filter((t) => t.status === "done").length}/{tasks.length}{" "}
            tâches terminées
          </p>
        </div>
        <div className="project-header-actions">
          <button className="ghost" onClick={() => setTab("sandbox")}>
            <Beaker /> Sandbox
          </button>
          <button className="ghost" onClick={() => setTab("communication")}>
            <Bell /> Communication
            {unreadNotifications > 0 && (
              <span className="notification-count">{unreadNotifications}</span>
            )}
          </button>
          {profile?.isAdmin && (
            <button
              className="button"
              onClick={publish}
              disabled={releaseDone !== releaseChecks.length}
            >
              <Rocket /> Publier
            </button>
          )}
        </div>
      </header>
      {notice && <div className="studio-notice">{notice}</div>}
      <nav className="studio-tabs studio-tabs-complete">
        {(
          [
            ["dashboard", "Vue d’ensemble"],
            ["blueprint", "Conception"],
            ["tasks", "Tâches"],
            ["workspace", "Espace de travail"],
            [
              "communication",
              `Communication${unreadNotifications ? ` (${unreadNotifications})` : ""}`,
            ],
            ["assistant", "Assistant IA"],
            ["content", "Énigmes"],
            [
              "qa",
              `Qualité${openIssues.length ? ` (${openIssues.length})` : ""}`,
            ],
            ["team", "Équipe"],
            ["sandbox", "Sandbox"],
            ["release", "Publication"],
          ] as Array<[StudioTab, string]>
        ).map(([id, label]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </nav>
      {tab === "dashboard" && (
        <Dashboard
          project={project}
          tasks={tasks}
          issues={issues}
          puzzles={puzzles}
          canEdit={can("manage_workflow")}
          onToggle={toggleStage}
          onOpen={setTab}
        />
      )}{" "}
      {tab === "blueprint" && (
        <BlueprintEditor
          project={project}
          canOverview={can("edit_overview")}
          canTechnical={can("edit_technical")}
          onNotice={setNotice}
        />
      )}{" "}
      {tab === "tasks" && (
        <TaskBoard
          project={project}
          tasks={tasks}
          sprints={sprints}
          userId={user.uid}
          canManage={can("manage_workflow")}
          onNotice={setNotice}
        />
      )}{" "}
      {tab === "workspace" && (
        <ProjectWorkspace
          project={project}
          items={workspaceItems}
          tasks={tasks}
          userId={user.uid}
          canApprove={can("approve_release")}
          onNotice={setNotice}
        />
      )}{" "}
      {tab === "communication" && (
        <CommunicationCenter
          project={project}
          userId={user.uid}
          notifications={notifications}
          canAnnounce={can("manage_workflow")}
          onNotice={setNotice}
        />
      )}{" "}
      {tab === "assistant" && (
        <StudioAiAssistant
          project={project}
          userId={user.uid}
          canApply={canApplyAi}
          onNotice={setNotice}
        />
      )}{" "}
      {tab === "content" && (
        <ContentEditor
          projectId={project.id}
          puzzles={puzzles}
          canEdit={can("edit_content")}
          onNotice={setNotice}
        />
      )}{" "}
      {tab === "qa" && (
        <QualityCenter
          project={project}
          issues={issues}
          puzzles={puzzles}
          canReport={can("test_sandbox")}
          onNotice={setNotice}
        />
      )}{" "}
      {tab === "team" && (
        <TeamAccess
          project={project}
          canManage={Boolean(profile?.isAdmin || can("manage_team"))}
          onNotice={setNotice}
        />
      )}{" "}
      {tab === "sandbox" && (
        <Sandbox
          project={project}
          puzzles={puzzles}
          allowed={can("test_sandbox")}
        />
      )}{" "}
      {tab === "release" && (
        <ReleaseCenter
          project={project}
          issues={issues}
          puzzles={puzzles}
          canApprove={can("approve_release")}
          isAdmin={Boolean(profile?.isAdmin)}
          onPublish={publish}
          onNotice={setNotice}
        />
      )}
    </section>
  );
}

const aiActionLabels: Record<StudioAiAction["type"], string> = {
  create_task: "Créer une tâche",
  create_workspace_document: "Créer un document",
  update_blueprint: "Mettre à jour la conception",
  create_puzzle: "Créer une énigme",
};

function StudioAiAssistant({
  project,
  userId,
  canApply,
  onNotice,
}: {
  project: GameProject;
  userId: string;
  canApply: boolean;
  onNotice: (message: string) => void;
}) {
  const [conversations, setConversations] = useState<StudioAiConversation[]>([]);
  const [messages, setMessages] = useState<StudioAiMessage[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const messageEnd = useRef<HTMLDivElement>(null);

  useEffect(
    () =>
      onSnapshot(
        query(
          collection(db, "gameProjects", project.id, "aiConversations"),
          orderBy("updatedAt", "desc"),
        ),
        (snapshot) => {
          const next = snapshot.docs.map(
            (item) => ({ id: item.id, ...item.data() }) as StudioAiConversation,
          );
          setConversations(next);
          setSelectedId((current) =>
            current && next.some((item) => item.id === current)
              ? current
              : next[0]?.id || "",
          );
        },
      ),
    [project.id],
  );

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }
    return onSnapshot(
      query(
        collection(
          db,
          "gameProjects",
          project.id,
          "aiConversations",
          selectedId,
          "messages",
        ),
        orderBy("createdAt", "asc"),
      ),
      (snapshot) =>
        setMessages(
          snapshot.docs.map(
            (item) => ({ id: item.id, ...item.data() }) as StudioAiMessage,
          ),
        ),
    );
  }, [project.id, selectedId]);

  useEffect(() => {
    messageEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  async function createConversation() {
    setBusy(true);
    try {
      const result = await call<{ conversationId: string }>(
        "createStudioAiConversation",
        { projectId: project.id },
      );
      setSelectedId(result.data.conversationId);
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Création de la conversation impossible.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function sendPrompt(event: FormEvent) {
    event.preventDefault();
    const body = prompt.trim();
    if (!body || busy) return;
    setBusy(true);
    setPrompt("");
    try {
      let conversationId = selectedId;
      if (!conversationId) {
        const created = await call<{ conversationId: string }>(
          "createStudioAiConversation",
          { projectId: project.id, title: body.slice(0, 70) },
        );
        conversationId = created.data.conversationId;
        setSelectedId(conversationId);
      }
      await call("askStudioAssistant", {
        projectId: project.id,
        conversationId,
        body,
      });
    } catch (error) {
      setPrompt(body);
      onNotice(
        error instanceof Error ? error.message : "L’assistant ne peut pas répondre.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function applyAction(messageId: string, action: StudioAiAction) {
    if (!canApply || action.status !== "proposed") return;
    setBusy(true);
    try {
      await call("applyStudioAiAction", {
        projectId: project.id,
        conversationId: selectedId,
        messageId,
        actionId: action.id,
      });
      onNotice(`${aiActionLabels[action.type]} : action appliquée.`);
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Application impossible.",
      );
    } finally {
      setBusy(false);
    }
  }

  const quickPrompts = [
    "Analyse l’état du projet et indique les prochaines priorités.",
    "Repère les incohérences dans la conception du jeu.",
    "Propose les tâches nécessaires pour la prochaine étape.",
  ];

  return (
    <section className="ai-workspace">
      <aside className="ai-conversation-list">
        <header>
          <div>
            <span className="eyebrow">Copilote du projet</span>
            <h2><Bot /> Assistant IA</h2>
          </div>
          <button className="icon-button" onClick={createConversation} disabled={busy} title="Nouvelle conversation">
            <Plus />
          </button>
        </header>
        <div className="ai-access-card">
          <ShieldCheck />
          <div>
            <strong>{canApply ? "Mode développeur" : "Mode consultation"}</strong>
            <span>
              {canApply
                ? "Vous pouvez examiner et appliquer les propositions de l’IA."
                : "Vous pouvez échanger avec l’IA. Un développeur doit appliquer ses propositions."}
            </span>
          </div>
        </div>
        <div className="ai-conversation-buttons">
          {conversations.map((conversation) => (
            <button
              key={conversation.id}
              className={selectedId === conversation.id ? "active" : ""}
              onClick={() => setSelectedId(conversation.id)}
            >
              <MessageSquare />
              <span>{conversation.title}</span>
            </button>
          ))}
          {!conversations.length && <p>Aucune conversation pour ce projet.</p>}
        </div>
      </aside>
      <div className="ai-chat-panel">
        <header className="ai-chat-header">
          <div>
            <span className="eyebrow">Contexte partagé</span>
            <h2>{project.title}</h2>
          </div>
          <span className="ai-context-badge"><Sparkles /> Blueprint, tâches, énigmes et documents</span>
        </header>
        <div className="ai-messages">
          {!messages.length && (
            <div className="ai-welcome">
              <Bot />
              <h3>Que voulez-vous faire avancer?</h3>
              <p>L’assistant connaît la structure actuelle du projet et peut proposer des ajouts sans les appliquer automatiquement.</p>
              <div className="ai-quick-prompts">
                {quickPrompts.map((item) => (
                  <button key={item} onClick={() => setPrompt(item)}>{item}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((message) => (
            <article className={`ai-message ${message.role}`} key={message.id}>
              <div className="ai-message-author">
                {message.role === "assistant" ? <Bot /> : <Users />}
                <strong>{message.role === "assistant" ? "Assistant IA" : message.authorName}</strong>
              </div>
              <p>{message.body}</p>
              {!!message.actions?.length && (
                <div className="ai-proposals">
                  {message.actions.map((action) => (
                    <section className={`ai-proposal ${action.status}`} key={action.id}>
                      <div>
                        <span>{aiActionLabels[action.type]}</span>
                        <h4>{action.title}</h4>
                        <p>{action.description}</p>
                      </div>
                      {action.status === "applied" ? (
                        <span className="ai-applied"><Check /> Appliquée</span>
                      ) : canApply ? (
                        <button className="button small" disabled={busy} onClick={() => applyAction(message.id, action)}>
                          Vérifier et appliquer
                        </button>
                      ) : (
                        <span className="ai-pending">En attente d’un développeur</span>
                      )}
                    </section>
                  ))}
                </div>
              )}
            </article>
          ))}
          {busy && <div className="ai-thinking"><Sparkles /> L’assistant travaille…</div>}
          <div ref={messageEnd} />
        </div>
        <form className="ai-composer" onSubmit={sendPrompt}>
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Demandez une analyse, un plan, une tâche, un document ou une énigme…"
            maxLength={6000}
            rows={3}
          />
          <button className="button" disabled={busy || !prompt.trim()}><Send /> Envoyer</button>
        </form>
        <p className="ai-disclaimer">Les changements ne sont jamais automatiques. Une proposition doit être appliquée par un développeur du projet.</p>
      </div>
    </section>
  );
}

const defaultNotificationPreferences: StudioNotificationPreferences = {
  inApp: true,
  email: true,
  mobile: true,
  mentions: true,
  assignments: true,
  dueDates: true,
  comments: true,
  validations: true,
  blocked: true,
  announcements: true,
  messages: true,
};

function SecureAudio({ attachment }: { attachment: StudioMessageAttachment }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let active = true;
    let objectUrl = "";
    getBlob(ref(storage, attachment.storagePath))
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.storagePath]);
  return url ? <audio controls src={url} /> : <span>Chargement du message vocal…</span>;
}

function CommunicationCenter({
  project,
  userId,
  notifications,
  canAnnounce,
  onNotice,
}: {
  project: GameProject;
  userId: string;
  notifications: StudioNotification[];
  canAnnounce: boolean;
  onNotice: (message: string) => void;
}) {
  const [conversations, setConversations] = useState<StudioConversation[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState<StudioMessage[]>([]);
  const [presence, setPresence] = useState<StudioPresence[]>([]);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<StudioMessageAttachment[]>([]);
  const [replyTo, setReplyTo] = useState<StudioMessage>();
  const [showCreate, setShowCreate] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const recorder = useRef<MediaRecorder | undefined>(undefined);
  const recordingChunks = useRef<Blob[]>([]);
  const typingTimer = useRef<number | undefined>(undefined);
  const [draft, setDraft] = useState({
    title: "",
    type: "channel" as StudioConversationType,
    participantIds: [] as string[],
  });
  const [preferences, setPreferences] = useState(
    defaultNotificationPreferences,
  );
  useEffect(
    () =>
      onSnapshot(
        query(
          collection(db, "gameProjects", project.id, "conversations"),
          where("visibleTo", "array-contains", userId),
        ),
        (snapshot) => {
          const next = snapshot.docs
            .map(
              (item) =>
                ({ id: item.id, ...item.data() }) as StudioConversation,
            )
            .sort(
              (a, b) =>
                (b.updatedAt?.toMillis?.() || 0) -
                (a.updatedAt?.toMillis?.() || 0),
            );
          setConversations(next);
          setSelectedId((current) => current || next[0]?.id || "");
        },
      ),
    [project.id, userId],
  );
  useEffect(
    () =>
      onSnapshot(
        collection(db, "gameProjects", project.id, "presence"),
        (snapshot) =>
          setPresence(
            snapshot.docs.map(
              (item) =>
                ({ id: item.id, ...item.data() }) as unknown as StudioPresence,
            ),
          ),
      ),
    [project.id],
  );
  useEffect(() => {
    const displayName = memberName(userId);
    const writePresence = (status: "online" | "away" | "offline", typing = false) =>
      setDoc(
        doc(db, "gameProjects", project.id, "presence", userId),
        {
          uid: userId,
          displayName,
          status,
          conversationId: selectedId,
          typing,
          lastSeen: serverTimestamp(),
        },
        { merge: true },
      );
    void writePresence(document.hidden ? "away" : "online");
    const heartbeat = window.setInterval(
      () => void writePresence(document.hidden ? "away" : "online"),
      30000,
    );
    const visibility = () =>
      void writePresence(document.hidden ? "away" : "online");
    document.addEventListener("visibilitychange", visibility);
    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", visibility);
      void writePresence("offline");
    };
  }, [project.id, selectedId, userId]);
  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }
    return onSnapshot(
      query(
        collection(
          db,
          "gameProjects",
          project.id,
          "conversations",
          selectedId,
          "messages",
        ),
        orderBy("createdAt", "asc"),
      ),
      (snapshot) =>
        setMessages(
          snapshot.docs.map(
            (item) => ({ id: item.id, ...item.data() }) as StudioMessage,
          ),
        ),
    );
  }, [project.id, selectedId]);
  useEffect(() => {
    if (!selectedId || !messages.length) return;
    const unread = messages.some((entry) => !entry.readBy?.includes(userId));
    if (unread)
      void call("markProjectConversationRead", {
        projectId: project.id,
        conversationId: selectedId,
      });
  }, [messages, project.id, selectedId, userId]);
  useEffect(() => {
    if (!notifications.some((item) => !item.read) || document.visibilityState === "visible")
      return;
    const latest = notifications.find((item) => !item.read);
    if (latest && "Notification" in window && Notification.permission === "granted")
      new Notification(latest.title, { body: latest.body, tag: latest.id });
  }, [notifications]);
  useEffect(
    () =>
      onSnapshot(
        doc(db, "users", userId, "studioSettings", "notifications"),
        (snapshot) =>
          setPreferences({
            ...defaultNotificationPreferences,
            ...(snapshot.exists() ? snapshot.data() : {}),
          }),
      ),
    [userId],
  );
  const selected = conversations.find((item) => item.id === selectedId);
  const memberName = (uid: string) =>
    project.members.find((member) => member.uid === uid)?.displayName ||
    project.members.find((member) => member.uid === uid)?.email ||
    "Membre";
  const dateLabel = (value?: { toDate?: () => Date }) =>
    value?.toDate?.().toLocaleString("fr-CA", {
      dateStyle: "short",
      timeStyle: "short",
    }) || "À l’instant";

  async function uploadAttachment(file: globalThis.File) {
    if (!file.size || file.size > 50 * 1024 * 1024)
      throw new Error("La pièce jointe doit peser moins de 50 Mo.");
    const fileId = crypto.randomUUID().replaceAll("-", "");
    const versionId = crypto.randomUUID().replaceAll("-", "");
    const safeName = file.name
      .replace(/[^\p{L}\p{N}._() -]+/gu, "_")
      .slice(0, 180);
    const storagePath = `studio/${project.id}/${fileId}/${versionId}/${safeName}`;
    const task = uploadBytesResumable(ref(storage, storagePath), file, {
      contentType: file.type || "application/octet-stream",
    });
    await new Promise<void>((resolve, reject) =>
      task.on("state_changed", undefined, reject, resolve),
    );
    await call("registerWorkspaceFileUpload", {
      projectId: project.id,
      fileId,
      versionId,
      folderId: "",
      name: file.name,
      storagePath,
    });
    return {
      fileId,
      name: file.name,
      size: file.size,
      contentType: file.type || "application/octet-stream",
      storagePath,
    } satisfies StudioMessageAttachment;
  }

  async function chooseAttachments(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files || []).slice(0, 8);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded: StudioMessageAttachment[] = [];
      for (const file of files) uploaded.push(await uploadAttachment(file));
      setAttachments((current) => [...current, ...uploaded].slice(0, 8));
      onNotice(`${uploaded.length} pièce(s) jointe(s) prête(s).`);
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Téléversement impossible.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function toggleRecording() {
    if (recording) {
      recorder.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      recordingChunks.current = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size) recordingChunks.current.push(event.data);
      };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(recordingChunks.current, { type: "audio/webm" });
        const voice = new File([blob], `message-vocal-${Date.now()}.webm`, {
          type: "audio/webm",
        });
        setUploading(true);
        try {
          const uploaded = await uploadAttachment(voice);
          setAttachments((current) => [...current, uploaded].slice(0, 8));
          onNotice("Message vocal prêt à envoyer.");
        } catch (error) {
          onNotice(error instanceof Error ? error.message : "Enregistrement impossible.");
        } finally {
          setUploading(false);
        }
      };
      recorder.current = mediaRecorder;
      mediaRecorder.start();
      setRecording(true);
    } catch {
      onNotice("Autorisez le microphone pour enregistrer un message vocal.");
    }
  }

  async function openAttachment(attachment: StudioMessageAttachment) {
    try {
      const blob = await getBlob(ref(storage, attachment.storagePath));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = attachment.name;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      onNotice("Impossible d’ouvrir cette pièce jointe.");
    }
  }

  function updateTyping(value: string) {
    setMessage(value);
    void setDoc(
      doc(db, "gameProjects", project.id, "presence", userId),
      {
        uid: userId,
        displayName: memberName(userId),
        status: "online",
        conversationId: selectedId,
        typing: true,
        lastSeen: serverTimestamp(),
      },
      { merge: true },
    );
    if (typingTimer.current) window.clearTimeout(typingTimer.current);
    typingTimer.current = window.setTimeout(
      () =>
        void setDoc(
          doc(db, "gameProjects", project.id, "presence", userId),
          {
            uid: userId,
            displayName: memberName(userId),
            status: "online",
            conversationId: selectedId,
            typing: false,
            lastSeen: serverTimestamp(),
          },
          { merge: true },
        ),
      1800,
    );
  }

  async function createConversation(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await call<{ conversationId: string }>(
        "createProjectConversation",
        { projectId: project.id, ...draft },
      );
      setSelectedId(result.data.conversationId);
      setDraft({ title: "", type: "channel", participantIds: [] });
      setShowCreate(false);
      onNotice("Conversation créée.");
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Création impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function sendMessage(event: FormEvent) {
    event.preventDefault();
    if (!selected || (!message.trim() && !attachments.length)) return;
    const normalized = message.toLocaleLowerCase("fr-CA");
    const mentionedUserIds = project.members
      .filter((member) => {
        const displayName = (member.displayName || "").toLocaleLowerCase("fr-CA");
        const emailName = member.email.split("@")[0].toLocaleLowerCase("fr-CA");
        return (
          (displayName && normalized.includes(`@${displayName}`)) ||
          normalized.includes(`@${emailName}`)
        );
      })
      .map((member) => member.uid);
    setBusy(true);
    try {
      await call("sendProjectMessage", {
        projectId: project.id,
        conversationId: selected.id,
        body: message,
        mentionedUserIds,
        replyToId: replyTo?.id || "",
        attachments,
      });
      setMessage("");
      setAttachments([]);
      setReplyTo(undefined);
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Envoi impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function react(messageId: string, emoji: string) {
    try {
      await call("reactToProjectMessage", {
        projectId: project.id,
        conversationId: selectedId,
        messageId,
        emoji,
      });
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Réaction impossible.");
    }
  }

  async function markRead(notification: StudioNotification) {
    if (!notification.read)
      await call("markStudioNotificationRead", {
        notificationId: notification.id,
      });
    if (notification.entityType === "conversation" && notification.entityId)
      setSelectedId(notification.entityId);
  }

  async function savePreferences(next: StudioNotificationPreferences) {
    setPreferences(next);
    try {
      await call("saveStudioNotificationPreferences", next);
      onNotice("Préférences de notification enregistrées.");
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Enregistrement impossible.",
      );
    }
  }

  function insertMention(uid: string) {
    const name = memberName(uid).replace(/\s+/g, " ").trim();
    setMessage((current) => `${current}${current ? " " : ""}@${name} `);
  }

  return (
    <div className="communication-shell">
      <aside className="communication-sidebar">
        <div className="communication-heading">
          <div>
            <span className="eyebrow">Centre d’équipe</span>
            <h2>Communication</h2>
          </div>
          <button className="icon-button" onClick={() => setShowCreate(true)}>
            <Plus />
          </button>
        </div>
        <div className="conversation-list">
          {conversations.map((conversation) => (
            <button
              className={selectedId === conversation.id ? "active" : ""}
              key={conversation.id}
              onClick={() => setSelectedId(conversation.id)}
            >
              {conversation.type === "announcement" ? (
                <Megaphone />
              ) : conversation.type === "direct" ? (
                <Users />
              ) : (
                <MessageSquare />
              )}
              <span>
                <strong>
                  {conversation.title}
                  {conversation.type === "direct" &&
                    conversation.participantIds
                      .filter((uid) => uid !== userId)
                      .some((uid) =>
                        presence.some(
                          (item) => item.uid === uid && item.status === "online",
                        ),
                      ) && <i className="presence-dot online" title="En ligne" />}
                </strong>
                <small>{conversation.lastMessage || "Aucun message"}</small>
              </span>
            </button>
          ))}
          {!conversations.length && (
            <p className="communication-empty">
              Créez un canal pour commencer les échanges du projet.
            </p>
          )}
        </div>
        <div className="notification-panel">
          <div className="communication-heading compact">
            <h3>
              <Bell /> Notifications
            </h3>
            <button
              className="text-button"
              onClick={() =>
                call("markAllStudioNotificationsRead", {
                  projectId: project.id,
                })
              }
            >
              Tout lire
            </button>
          </div>
          {notifications.slice(0, 12).map((notification) => (
            <button
              className={`notification-item ${notification.read ? "" : "unread"}`}
              key={notification.id}
              onClick={() => markRead(notification)}
            >
              <span className="notification-dot" />
              <span>
                <strong>{notification.title}</strong>
                <small>{notification.body}</small>
              </span>
            </button>
          ))}
          {!notifications.length && (
            <p className="communication-empty">Aucune notification.</p>
          )}
          <button
            className="preferences-button"
            onClick={() => setShowPreferences((current) => !current)}
          >
            Préférences de notification
          </button>
        </div>
      </aside>
      <main className="conversation-main">
        {selected ? (
          <>
            <header>
              <div>
                <span className="eyebrow">{selected.type}</span>
                <h2>{selected.title}</h2>
                <div className="presence-list">
                  {selected.visibleTo.slice(0, 8).map((uid) => {
                    const state = presence.find((item) => item.uid === uid)?.status || "offline";
                    return (
                      <span key={uid} title={`${memberName(uid)} · ${state}`}>
                        {memberName(uid).slice(0, 1).toUpperCase()}
                        <i className={`presence-dot ${state}`} />
                      </span>
                    );
                  })}
                </div>
              </div>
              <span>{selected.visibleTo.length} participant(s)</span>
            </header>
            <div className="message-stream">
              {messages.map((entry) => {
                const replied = messages.find(
                  (candidate) => candidate.id === entry.replyToId,
                );
                return (
                  <article
                    className={entry.authorId === userId ? "mine" : ""}
                    key={entry.id}
                  >
                    <div className="message-avatar">
                      {entry.authorName.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="message-content">
                      <div>
                        <strong>{entry.authorName}</strong>
                        <time>{dateLabel(entry.createdAt)}</time>
                      </div>
                      {replied && (
                        <blockquote>
                          {replied.authorName} · {replied.body.slice(0, 140)}
                        </blockquote>
                      )}
                      <p>{entry.body}</p>
                      {entry.attachments?.length ? (
                        <div className="message-attachments">
                          {entry.attachments.map((attachment) =>
                            attachment.contentType.startsWith("audio/") ? (
                              <SecureAudio key={attachment.fileId} attachment={attachment} />
                            ) : (
                              <button
                                key={attachment.fileId}
                                onClick={() => openAttachment(attachment)}
                              >
                                <Paperclip />
                                <span>{attachment.name}</span>
                                <small>
                                  {(attachment.size / 1024 / 1024).toLocaleString("fr-CA", {
                                    maximumFractionDigits: 1,
                                  })} Mo
                                </small>
                              </button>
                            ),
                          )}
                        </div>
                      ) : null}
                      <div className="message-actions">
                        {["👍", "❤️", "✅"].map((emoji) => {
                          const count =
                            entry.reactions?.find((item) => item.emoji === emoji)
                              ?.userIds.length || 0;
                          return (
                            <button key={emoji} onClick={() => react(entry.id, emoji)}>
                              {emoji} {count || ""}
                            </button>
                          );
                        })}
                        <button onClick={() => setReplyTo(entry)}>
                          <Reply /> Répondre
                        </button>
                        {entry.authorId === userId && (
                          <span className="read-receipt" title="Accusé de lecture">
                            ✓✓ {Math.max(0, (entry.readBy?.length || 1) - 1)}
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
              {!messages.length && (
                <div className="conversation-welcome">
                  <MessageSquare />
                  <h3>La conversation commence ici</h3>
                  <p>Partagez une décision, une question ou une mise à jour.</p>
                </div>
              )}
            </div>
            {presence.some(
              (item) =>
                item.uid !== userId &&
                item.conversationId === selectedId &&
                item.typing,
            ) && (
              <div className="typing-indicator">
                {presence
                  .filter(
                    (item) =>
                      item.uid !== userId &&
                      item.conversationId === selectedId &&
                      item.typing,
                  )
                  .map((item) => item.displayName)
                  .join(", ")} écrit…
              </div>
            )}
            <div className="mention-shortcuts">
              <AtSign />
              {project.members
                .filter((member) => member.uid !== userId)
                .slice(0, 8)
                .map((member) => (
                  <button key={member.uid} onClick={() => insertMention(member.uid)}>
                    {member.displayName || member.email.split("@")[0]}
                  </button>
                ))}
            </div>
            <form className="message-composer" onSubmit={sendMessage}>
              {replyTo && (
                <div className="reply-preview">
                  <Reply /> Réponse à {replyTo.authorName}
                  <button type="button" onClick={() => setReplyTo(undefined)}>
                    <X />
                  </button>
                </div>
              )}
              {attachments.length > 0 && (
                <div className="attachment-preview">
                  {attachments.map((attachment) => (
                    <span key={attachment.fileId}>
                      <Paperclip /> {attachment.name}
                      <button
                        type="button"
                        onClick={() =>
                          setAttachments((current) =>
                            current.filter((item) => item.fileId !== attachment.fileId),
                          )
                        }
                      >
                        <X />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <textarea
                value={message}
                onChange={(event) => updateTyping(event.target.value)}
                placeholder={
                  selected.type === "announcement"
                    ? "Publier une annonce…"
                    : "Écrire un message…"
                }
                disabled={selected.type === "announcement" && !canAnnounce}
              />
              <div className="composer-tools">
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  accept="image/*,audio/*,video/*,application/pdf,.zip,.rar,.7z"
                  onChange={chooseAttachments}
                  hidden
                />
                <button
                  type="button"
                  className="icon-button"
                  title="Joindre un fichier"
                  onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                >
                  <Paperclip />
                </button>
                <button
                  type="button"
                  className={`icon-button ${recording ? "recording" : ""}`}
                  title={recording ? "Arrêter l’enregistrement" : "Message vocal"}
                  onClick={toggleRecording}
                  disabled={uploading}
                >
                  {recording ? <StopCircle /> : <Mic />}
                </button>
              </div>
              <button
                className="button"
                disabled={busy || uploading || (!message.trim() && !attachments.length) || (selected.type === "announcement" && !canAnnounce)}
              >
                <Send /> Envoyer
              </button>
            </form>
          </>
        ) : (
          <div className="conversation-welcome full">
            <MessageSquare />
            <h2>Un espace commun pour faire avancer le projet</h2>
            <p>Créez un canal d’équipe, une annonce ou une discussion privée.</p>
            <button className="button" onClick={() => setShowCreate(true)}>
              <Plus /> Créer une conversation
            </button>
          </div>
        )}
      </main>
      {showCreate && (
        <div className="ticket-modal" onMouseDown={(event) => event.target === event.currentTarget && setShowCreate(false)}>
          <form className="project-form communication-modal" onSubmit={createConversation}>
            <header>
              <div>
                <span className="eyebrow">Nouvel espace</span>
                <h2>Créer une conversation</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setShowCreate(false)}>
                <X />
              </button>
            </header>
            <label>
              Type
              <select
                value={draft.type}
                onChange={(event) => setDraft({ ...draft, type: event.target.value as StudioConversationType, participantIds: [] })}
              >
                <option value="channel">Canal d’équipe</option>
                <option value="direct">Message privé</option>
                {canAnnounce && <option value="announcement">Annonces</option>}
              </select>
            </label>
            <label>
              Nom
              <input
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                placeholder={draft.type === "direct" ? "Discussion privée" : "Ex. Production terrain"}
                required
              />
            </label>
            {draft.type === "direct" && (
              <fieldset className="participant-picker">
                <legend>Participants</legend>
                {project.members.filter((member) => member.uid !== userId).map((member) => (
                  <label key={member.uid}>
                    <input
                      type="checkbox"
                      checked={draft.participantIds.includes(member.uid)}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          participantIds: event.target.checked
                            ? [...draft.participantIds, member.uid]
                            : draft.participantIds.filter((uid) => uid !== member.uid),
                        })
                      }
                    />
                    {member.displayName || member.email}
                  </label>
                ))}
              </fieldset>
            )}
            <button className="button full" disabled={busy}>Créer</button>
          </form>
        </div>
      )}
      {showPreferences && (
        <div className="preferences-drawer">
          <div className="communication-heading">
            <div>
              <span className="eyebrow">Réglages personnels</span>
              <h2>Notifications</h2>
            </div>
            <button className="icon-button" onClick={() => setShowPreferences(false)}><X /></button>
          </div>
          <h3>Canaux</h3>
          {"Notification" in window && Notification.permission !== "granted" && (
            <button
              className="button full"
              onClick={() =>
                Notification.requestPermission().then((permission) =>
                  onNotice(
                    permission === "granted"
                      ? "Notifications de l’appareil activées."
                      : "Notifications de l’appareil refusées.",
                  ),
                )
              }
            >
              <Bell /> Activer sur cet appareil
            </button>
          )}
          {(["inApp", "email", "mobile"] as const).map((key) => (
            <label key={key}>
              <span>{key === "inApp" ? "Dans l’application" : key === "email" ? "Courriel" : "Mobile"}</span>
              <input
                type="checkbox"
                checked={preferences[key]}
                onChange={(event) => savePreferences({ ...preferences, [key]: event.target.checked })}
              />
            </label>
          ))}
          <h3>Événements</h3>
          {([
            ["mentions", "Mentions"],
            ["assignments", "Affectations"],
            ["dueDates", "Échéances"],
            ["comments", "Commentaires"],
            ["validations", "Validations"],
            ["blocked", "Blocages"],
            ["announcements", "Annonces"],
            ["messages", "Messages"],
          ] as Array<[keyof StudioNotificationPreferences, string]>).map(([key, label]) => (
            <label key={key}>
              <span>{label}</span>
              <input
                type="checkbox"
                checked={preferences[key]}
                onChange={(event) => savePreferences({ ...preferences, [key]: event.target.checked })}
              />
            </label>
          ))}
          <p className="muted">Les notifications dans l’application sont actives. Les canaux courriel et mobile sont prêts pour le branchement aux services d’envoi.</p>
        </div>
      )}
    </div>
  );
}

const workspaceTypeLabels: Record<WorkspaceItemType, string> = {
  document: "Document",
  resource: "Ressource",
  discussion: "Discussion",
  whiteboard: "Idée",
  decision: "Décision",
  event: "Événement",
};
const workspaceStatusLabels: Record<WorkspaceItemStatus, string> = {
  draft: "Brouillon",
  review: "En révision",
  approved: "Approuvé",
  archived: "Archivé",
};

function ProjectWorkspace({
  project,
  items,
  tasks,
  userId,
  canApprove,
  onNotice,
}: {
  project: GameProject;
  items: WorkspaceItem[];
  tasks: StudioTask[];
  userId: string;
  canApprove: boolean;
  onNotice: (message: string) => void;
}) {
  type WorkspaceView =
    | "overview"
    | "documents"
    | "resources"
    | "discussions"
    | "whiteboard"
    | "decisions"
    | "calendar"
    | "files"
    | "trash"
    | "mine";
  const emptyDraft = {
    type: "document" as WorkspaceItemType,
    title: "",
    body: "",
    url: "",
    status: "draft" as WorkspaceItemStatus,
    assigneeId: "",
    linkedTaskId: "",
    eventDate: "",
    labelsText: "",
    color: "gold",
    versionSummary: "",
  };
  const [view, setView] = useState<WorkspaceView>("overview");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(emptyDraft);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState<WorkspaceItem>();
  const [selected, setSelected] = useState<WorkspaceItem>();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!selected) return;
    const current = items.find((item) => item.id === selected.id);
    if (current && current !== selected) setSelected(current);
  }, [items, selected]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (item.status === "archived" && view !== "mine") return false;
      const viewMatch =
        view === "overview" ||
        (view === "documents" && item.type === "document") ||
        (view === "resources" && item.type === "resource") ||
        (view === "discussions" && item.type === "discussion") ||
        (view === "whiteboard" && item.type === "whiteboard") ||
        (view === "decisions" && item.type === "decision") ||
        (view === "calendar" && item.type === "event") ||
        (view === "mine" &&
          (item.assigneeId === userId || item.createdBy === userId));
      return (
        viewMatch &&
        (!q ||
          `${item.title} ${item.body} ${(item.labels || []).join(" ")}`
            .toLowerCase()
            .includes(q))
      );
    });
  }, [items, search, userId, view]);
  const approved = items.filter((item) => item.status === "approved").length;
  const reviews = items.filter((item) => item.status === "review").length;
  const upcoming = items
    .filter((item) => item.type === "event" && item.eventDate)
    .sort((a, b) => String(a.eventDate).localeCompare(String(b.eventDate)));

  function openCreate(type: WorkspaceItemType) {
    setEditing(undefined);
    setDraft({ ...emptyDraft, type });
    setShowEditor(true);
  }
  function openEdit(item: WorkspaceItem) {
    setEditing(item);
    setSelected(undefined);
    setShowEditor(true);
    setDraft({
      type: item.type,
      title: item.title,
      body: item.body,
      url: item.url || "",
      status: item.status,
      assigneeId: item.assigneeId || "",
      linkedTaskId: item.linkedTaskId || "",
      eventDate: item.eventDate || "",
      labelsText: (item.labels || []).join(", "),
      color: item.color || "gold",
      versionSummary: "",
    });
  }
  async function saveItem(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await call("saveWorkspaceItem", {
        projectId: project.id,
        itemId: editing?.id,
        ...draft,
      });
      onNotice(
        editing
          ? "Élément mis à jour."
          : "Élément ajouté à l’espace de travail.",
      );
      setEditing(undefined);
      setDraft(emptyDraft);
      setShowEditor(false);
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Enregistrement impossible.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function changeStatus(
    item: WorkspaceItem,
    status: WorkspaceItemStatus,
  ) {
    setBusy(true);
    try {
      await call("saveWorkspaceItem", {
        projectId: project.id,
        itemId: item.id,
        ...item,
        status,
        labels: item.labels || [],
        versionSummary: `Statut : ${workspaceStatusLabels[status]}`,
      });
      setSelected(undefined);
      onNotice(`Statut changé pour « ${workspaceStatusLabels[status]} ».`);
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Modification impossible.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function addComment(event: FormEvent) {
    event.preventDefault();
    if (!selected || !comment.trim()) return;
    setBusy(true);
    try {
      await call("addWorkspaceComment", {
        projectId: project.id,
        itemId: selected.id,
        body: comment,
        mentionedUserIds: mentionedMembers(comment, project),
      });
      setComment("");
      onNotice("Commentaire ajouté.");
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Commentaire impossible.",
      );
    } finally {
      setBusy(false);
    }
  }
  function timeLabel(value: WorkspaceItem["updatedAt"]) {
    const date = value?.toDate?.();
    return date ? date.toLocaleDateString("fr-CA") : "Aujourd’hui";
  }
  const views: Array<[WorkspaceView, string]> = [
    ["overview", "Accueil"],
    ["documents", "Documents"],
    ["resources", "Ressources"],
    ["discussions", "Discussions"],
    ["whiteboard", "Tableau blanc"],
    ["decisions", "Décisions"],
    ["calendar", "Calendrier"],
    ["files", "Fichiers"],
    ["trash", "Corbeille"],
    ["mine", "Mon espace"],
  ];
  return (
    <section className="workspace-shell">
      <header className="workspace-header">
        <div>
          <span className="eyebrow">Collaboration centralisée</span>
          <h2>Espace de travail</h2>
          <p>
            Documents, échanges, décisions et ressources de {project.title}.
          </p>
        </div>
        <button className="button" onClick={() => openCreate("document")}>
          <Plus /> Créer
        </button>
      </header>
      <div className="workspace-metrics">
        <button onClick={() => setView("documents")}>
          <BookOpen />
          <strong>{items.filter((i) => i.type === "document").length}</strong>
          <span>documents</span>
        </button>
        <button onClick={() => setView("discussions")}>
          <MessageSquare />
          <strong>{items.filter((i) => i.type === "discussion").length}</strong>
          <span>discussions</span>
        </button>
        <button onClick={() => setView("decisions")}>
          <ClipboardCheck />
          <strong>{approved}</strong>
          <span>éléments approuvés</span>
        </button>
        <button onClick={() => setView("mine")}>
          <Users />
          <strong>
            {
              items.filter(
                (i) => i.assigneeId === userId || i.createdBy === userId,
              ).length
            }
          </strong>
          <span>dans mon espace</span>
        </button>
      </div>
      <div className="workspace-layout">
        <aside className="workspace-nav">
          {views.map(([id, label]) => (
            <button
              key={id}
              className={view === id ? "active" : ""}
              onClick={() => setView(id)}
            >
              {label}
              {id === "decisions" && reviews > 0 ? <b>{reviews}</b> : null}
            </button>
          ))}
          <div className="workspace-quick-create">
            <small>AJOUT RAPIDE</small>
            <button onClick={() => openCreate("document")}>
              <Plus /> Document
            </button>
            <button onClick={() => openCreate("resource")}>
              <Plus /> Ressource
            </button>
            <button onClick={() => openCreate("discussion")}>
              <Plus /> Discussion
            </button>
            <button onClick={() => openCreate("whiteboard")}>
              <Plus /> Idée
            </button>
            <button onClick={() => openCreate("decision")}>
              <Plus /> Décision
            </button>
            <button onClick={() => openCreate("event")}>
              <Plus /> Événement
            </button>
          </div>
        </aside>
        <div className="workspace-main">
          {view !== "files" && view !== "trash" && (
            <label className="workspace-search">
              <Search />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher dans l’espace de travail…"
              />
            </label>
          )}
          {view === "overview" && (
            <div className="workspace-overview">
              <section>
                <div className="workspace-section-heading">
                  <div>
                    <small>ACTIVITÉ</small>
                    <h3>Travail récent</h3>
                  </div>
                </div>
                <WorkspaceCards
                  items={visible.slice(0, 8)}
                  tasks={tasks}
                  onOpen={setSelected}
                />
              </section>
              <aside>
                <small>PROCHAINES ÉCHÉANCES</small>
                <h3>Calendrier</h3>
                {upcoming.slice(0, 5).map((item) => (
                  <button key={item.id} onClick={() => setSelected(item)}>
                    <CalendarDays />
                    <span>
                      <b>{item.title}</b>
                      <small>{item.eventDate?.replace("T", " · ")}</small>
                    </span>
                  </button>
                ))}
                {!upcoming.length && (
                  <p className="workspace-empty">Aucune échéance inscrite.</p>
                )}
              </aside>
            </div>
          )}
          {view !== "overview" &&
            view !== "whiteboard" &&
            view !== "calendar" &&
            view !== "files" &&
            view !== "trash" && (
              <>
                <div className="workspace-section-heading">
                  <div>
                    <small>
                      {visible.length} ÉLÉMENT{visible.length === 1 ? "" : "S"}
                    </small>
                    <h3>{views.find(([id]) => id === view)?.[1]}</h3>
                  </div>
                </div>
                <WorkspaceCards
                  items={visible}
                  tasks={tasks}
                  onOpen={setSelected}
                />
              </>
            )}
          {view === "whiteboard" && (
            <>
              <div className="workspace-section-heading">
                <div>
                  <small>IDÉATION</small>
                  <h3>Tableau blanc</h3>
                </div>
                <button
                  className="ghost"
                  onClick={() => openCreate("whiteboard")}
                >
                  <Plus /> Ajouter une note
                </button>
              </div>
              <div className="whiteboard-grid">
                {visible.map((item) => (
                  <button
                    key={item.id}
                    className={`whiteboard-note color-${item.color || "gold"}`}
                    onClick={() => setSelected(item)}
                  >
                    <Sparkles />
                    <strong>{item.title}</strong>
                    <p>{item.body}</p>
                    <small>{item.createdByName || "Membre"}</small>
                  </button>
                ))}
                {!visible.length && (
                  <p className="workspace-empty">
                    Ajoutez les premières idées du projet.
                  </p>
                )}
              </div>
            </>
          )}
          {view === "calendar" && (
            <>
              <div className="workspace-section-heading">
                <div>
                  <small>PLANIFICATION</small>
                  <h3>Calendrier du projet</h3>
                </div>
                <button className="ghost" onClick={() => openCreate("event")}>
                  <Plus /> Ajouter une date
                </button>
              </div>
              <div className="workspace-calendar">
                {visible
                  .sort((a, b) =>
                    String(a.eventDate).localeCompare(String(b.eventDate)),
                  )
                  .map((item) => (
                    <button key={item.id} onClick={() => setSelected(item)}>
                      <time>
                        {item.eventDate?.slice(0, 10) || "Date à définir"}
                      </time>
                      <span>
                        <strong>{item.title}</strong>
                        <small>{item.body}</small>
                      </span>
                      <b>
                        {item.assigneeId
                          ? project.members.find(
                              (m) => m.uid === item.assigneeId,
                            )?.displayName
                          : "Équipe"}
                      </b>
                    </button>
                  ))}
                {!visible.length && (
                  <p className="workspace-empty">Aucune date planifiée.</p>
                )}
              </div>
            </>
          )}
          {view === "files" && (
            <ProjectFiles project={project} onNotice={onNotice} />
          )}
          {view === "trash" && (
            <ProjectFiles project={project} trash onNotice={onNotice} />
          )}
        </div>
      </div>
      {showEditor && (
        <div
          className="ticket-modal"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowEditor(false);
              setEditing(undefined);
              setDraft(emptyDraft);
            }
          }}
        >
          <form
            className="project-form ticket-editor workspace-editor"
            onSubmit={saveItem}
          >
            <header>
              <div>
                <span className="eyebrow">
                  {editing ? "Modifier" : "Nouvel élément"}
                </span>
                <h2>{workspaceTypeLabels[draft.type]}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => {
                  setShowEditor(false);
                  setEditing(undefined);
                  setDraft(emptyDraft);
                }}
              >
                <X />
              </button>
            </header>
            <div className="workspace-form-grid">
              <label>
                Type
                <select
                  value={draft.type}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      type: e.target.value as WorkspaceItemType,
                    })
                  }
                >
                  {Object.entries(workspaceTypeLabels).map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Statut
                <select
                  value={draft.status}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      status: e.target.value as WorkspaceItemStatus,
                    })
                  }
                >
                  <option value="draft">Brouillon</option>
                  <option value="review">En révision</option>
                  {canApprove && <option value="approved">Approuvé</option>}
                  <option value="archived">Archivé</option>
                </select>
              </label>
              <label className="wide">
                Titre
                <input
                  required
                  minLength={2}
                  maxLength={180}
                  value={draft.title}
                  onChange={(e) =>
                    setDraft({ ...draft, title: e.target.value })
                  }
                />
              </label>
              <label className="wide">
                Contenu
                <textarea
                  maxLength={12000}
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  placeholder="Notes, contexte, description ou compte rendu…"
                />
              </label>
              {(draft.type === "resource" || draft.type === "document") && (
                <label className="wide">
                  Lien vers le fichier ou la ressource
                  <input
                    value={draft.url}
                    onChange={(e) =>
                      setDraft({ ...draft, url: e.target.value })
                    }
                    placeholder="https://…"
                  />
                </label>
              )}
              {draft.type === "event" && (
                <label>
                  Date et heure
                  <input
                    type="datetime-local"
                    required
                    value={draft.eventDate}
                    onChange={(e) =>
                      setDraft({ ...draft, eventDate: e.target.value })
                    }
                  />
                </label>
              )}
              <label>
                Responsable
                <select
                  value={draft.assigneeId}
                  onChange={(e) =>
                    setDraft({ ...draft, assigneeId: e.target.value })
                  }
                >
                  <option value="">Toute l’équipe</option>
                  {project.members.map((m) => (
                    <option key={m.uid} value={m.uid}>
                      {m.displayName || m.email}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Ticket lié
                <select
                  value={draft.linkedTaskId}
                  onChange={(e) =>
                    setDraft({ ...draft, linkedTaskId: e.target.value })
                  }
                >
                  <option value="">Aucun</option>
                  {tasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.key || task.id.slice(0, 6)} · {task.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Étiquettes
                <input
                  value={draft.labelsText}
                  onChange={(e) =>
                    setDraft({ ...draft, labelsText: e.target.value })
                  }
                  placeholder="narration, mobile, urgent"
                />
              </label>
              {draft.type === "whiteboard" && (
                <label>
                  Couleur
                  <select
                    value={draft.color}
                    onChange={(e) =>
                      setDraft({ ...draft, color: e.target.value })
                    }
                  >
                    <option value="gold">Or</option>
                    <option value="blue">Bleu</option>
                    <option value="green">Vert</option>
                    <option value="red">Rouge</option>
                  </select>
                </label>
              )}
              {editing && (
                <label className="wide">
                  Résumé de la version
                  <input
                    value={draft.versionSummary}
                    onChange={(e) =>
                      setDraft({ ...draft, versionSummary: e.target.value })
                    }
                    placeholder="Ce qui a été modifié"
                  />
                </label>
              )}
            </div>
            <button className="button" disabled={busy}>
              <Save /> {busy ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}
      {selected && (
        <div
          className="ticket-modal"
          onMouseDown={(e) =>
            e.target === e.currentTarget && setSelected(undefined)
          }
        >
          <article className="project-form ticket-editor workspace-detail">
            <header>
              <div>
                <span className="eyebrow">
                  {workspaceTypeLabels[selected.type]}
                </span>
                <h2>{selected.title}</h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setSelected(undefined)}
              >
                <X />
              </button>
            </header>
            <div className="workspace-detail-grid">
              <main>
                <div className="workspace-detail-meta">
                  <span className={`workspace-status ${selected.status}`}>
                    {workspaceStatusLabels[selected.status]}
                  </span>
                  <span>Mis à jour {timeLabel(selected.updatedAt)}</span>
                  {selected.linkedTaskId && (
                    <span>
                      Ticket{" "}
                      {tasks.find((t) => t.id === selected.linkedTaskId)?.key ||
                        "lié"}
                    </span>
                  )}
                </div>
                <p className="workspace-body">
                  {selected.body || "Aucun contenu."}
                </p>
                {selected.url && (
                  <a
                    className="button workspace-link"
                    href={selected.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Link2 /> Ouvrir la ressource
                  </a>
                )}
                <section className="ticket-comments">
                  <h3>
                    <MessageSquare /> Discussion (
                    {selected.comments?.length || 0})
                  </h3>
                  {selected.comments?.map((entry) => (
                    <article key={entry.id}>
                      <strong>{entry.authorName}</strong>
                      <p>{entry.body}</p>
                    </article>
                  ))}
                  <form className="comment-composer" onSubmit={addComment}>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Ajouter un commentaire…"
                    />
                    <button
                      className="button"
                      disabled={busy || !comment.trim()}
                    >
                      Envoyer
                    </button>
                  </form>
                </section>
              </main>
              <aside>
                <h3>Actions</h3>
                <button className="ghost" onClick={() => openEdit(selected)}>
                  <FilePenLine /> Modifier
                </button>
                <button
                  className="ghost"
                  onClick={() => changeStatus(selected, "review")}
                  disabled={busy}
                >
                  Demander une révision
                </button>
                {canApprove && (
                  <button
                    className="button"
                    onClick={() => changeStatus(selected, "approved")}
                    disabled={busy}
                  >
                    <Check /> Approuver
                  </button>
                )}
                <button
                  className="ghost"
                  onClick={() => changeStatus(selected, "archived")}
                  disabled={busy}
                >
                  Archiver
                </button>
                <h3>Historique</h3>
                <div className="workspace-history">
                  {selected.versions
                    ?.slice()
                    .reverse()
                    .map((version) => (
                      <div key={version.id}>
                        <Circle />
                        <span>
                          <b>{version.summary}</b>
                          <small>{version.authorName}</small>
                        </span>
                      </div>
                    ))}
                </div>
              </aside>
            </div>
          </article>
        </div>
      )}
    </section>
  );
}

function WorkspaceCards({
  items,
  tasks,
  onOpen,
}: {
  items: WorkspaceItem[];
  tasks: StudioTask[];
  onOpen: (item: WorkspaceItem) => void;
}) {
  if (!items.length)
    return <p className="workspace-empty">Aucun élément dans cette section.</p>;
  return (
    <div className="workspace-card-list">
      {items.map((item) => (
        <button key={item.id} onClick={() => onOpen(item)}>
          <span className={`workspace-type type-${item.type}`}>
            {workspaceTypeLabels[item.type]}
          </span>
          <div>
            <strong>{item.title}</strong>
            <p>{item.body || "Aucune description"}</p>
            <small>
              {item.createdByName || "Membre"}
              {item.linkedTaskId
                ? ` · ${tasks.find((t) => t.id === item.linkedTaskId)?.key || "Ticket lié"}`
                : ""}
            </small>
          </div>
          <span className={`workspace-status ${item.status}`}>
            {workspaceStatusLabels[item.status]}
          </span>
        </button>
      ))}
    </div>
  );
}

function Dashboard({
  project,
  tasks,
  issues,
  puzzles,
  canEdit,
  onToggle,
  onOpen,
}: {
  project: GameProject;
  tasks: StudioTask[];
  issues: StudioIssue[];
  puzzles: DraftPuzzle[];
  canEdit: boolean;
  onToggle: (id: ProductionStageId) => void;
  onOpen: (tab: StudioTab) => void;
}) {
  const completed = project.completedStages?.length || 0;
  const taskDone = tasks.filter((t) => t.status === "done").length;
  const blockers = issues.filter(
    (i) =>
      i.severity === "blocker" && !["resolved", "verified"].includes(i.status),
  ).length;
  return (
    <div className="studio-dashboard">
      <section className="studio-metrics">
        <button onClick={() => onOpen("blueprint")}>
          <BookOpen />
          <strong>{Math.round((completed / stages.length) * 100)}%</strong>
          <span>Production</span>
        </button>
        <button onClick={() => onOpen("tasks")}>
          <ListChecks />
          <strong>
            {taskDone}/{tasks.length}
          </strong>
          <span>Tâches</span>
        </button>
        <button onClick={() => onOpen("content")}>
          <Sparkles />
          <strong>{puzzles.length}</strong>
          <span>Énigmes</span>
        </button>
        <button onClick={() => onOpen("qa")}>
          <Bug />
          <strong>{blockers}</strong>
          <span>Blocages</span>
        </button>
      </section>
      <div className="studio-layout">
        <section className="workflow-list">
          <div className="studio-section-title">
            <div>
              <span className="eyebrow">Portes de production</span>
              <h2>Cycle complet</h2>
            </div>
          </div>
          {stages.map((stage) => {
            const done = project.completedStages?.includes(stage.id);
            const stageTasks = tasks.filter((t) => t.stageId === stage.id);
            return (
              <article
                className={`workflow-step workflow-detailed ${done ? "done" : ""}`}
                key={stage.id}
              >
                <button disabled={!canEdit} onClick={() => onToggle(stage.id)}>
                  {done ? <Check /> : <Circle />}
                </button>
                <div>
                  <h3>{stage.label}</h3>
                  <p>{stage.description}</p>
                  <div className="deliverable-list">
                    {stage.deliverables.map((item) => (
                      <span key={item}>
                        <CheckCircle2 />
                        {item}
                      </span>
                    ))}
                  </div>
                  {stageTasks.length > 0 && (
                    <small>
                      {stageTasks.filter((t) => t.status === "done").length}/
                      {stageTasks.length} tâches terminées
                    </small>
                  )}
                </div>
                <span>{done ? "Validée" : "À valider"}</span>
              </article>
            );
          })}
        </section>
        <aside className="panel readiness-panel">
          <h2>
            <ClipboardCheck /> Prochaines actions
          </h2>
          {blockers > 0 && (
            <div className="readiness-warning">
              <AlertTriangle /> {blockers} anomalie bloquante
            </div>
          )}
          {tasks
            .filter((t) => t.status !== "done")
            .slice(0, 6)
            .map((t) => (
              <button key={t.id} onClick={() => onOpen("tasks")}>
                <span className={`priority-dot ${t.priority}`} />
                <div>
                  <strong>{t.title}</strong>
                  <small>{taskStatusLabels[t.status]}</small>
                </div>
              </button>
            ))}
          {tasks.length === 0 && (
            <p className="muted">Créez les premières tâches du projet.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

function BlueprintEditor({
  project,
  canOverview,
  canTechnical,
  onNotice,
}: {
  project: GameProject;
  canOverview: boolean;
  canTechnical: boolean;
  onNotice: (s: string) => void;
}) {
  const [groupId, setGroupId] = useState(blueprintGroups[0].id);
  const group = blueprintGroups.find((g) => g.id === groupId)!;
  const [values, setValues] = useState<GameBlueprint>(project.blueprint || {});
  useEffect(() => setValues(project.blueprint || {}), [project.blueprint]);
  const all = blueprintGroups.flatMap((g) => g.fields);
  const filled = all.filter((f) => String(values[f.key] || "").trim()).length;
  async function save(e: FormEvent) {
    e.preventDefault();
    const payload: Record<string, string> = {};
    group.fields.forEach((f) => (payload[f.key] = String(values[f.key] || "")));
    try {
      await call("saveGameBlueprint", {
        projectId: project.id,
        values: payload,
      });
      onNotice(`${group.title} enregistrée.`);
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Enregistrement impossible.",
      );
    }
  }
  const canGroup = group.fields.every((f) => f.technical)
    ? canTechnical
    : canOverview;
  const Icon = group.icon;
  return (
    <div className="blueprint-shell">
      <aside className="blueprint-nav">
        <div className="blueprint-progress">
          <strong>{Math.round((filled / all.length) * 100)}%</strong>
          <span>Bible complétée</span>
          <div className="progress">
            <span style={{ width: `${(filled / all.length) * 100}%` }} />
          </div>
        </div>
        {blueprintGroups.map((item) => {
          const ItemIcon = item.icon;
          return (
            <button
              key={item.id}
              className={groupId === item.id ? "active" : ""}
              onClick={() => setGroupId(item.id)}
            >
              <ItemIcon />
              <div>
                <strong>{item.title}</strong>
                <small>
                  {
                    item.fields.filter((f) =>
                      String(values[f.key] || "").trim(),
                    ).length
                  }
                  /{item.fields.length} rubriques
                </small>
              </div>
            </button>
          );
        })}
      </aside>
      <form className="panel blueprint-form" onSubmit={save}>
        <div className="blueprint-heading">
          <div>
            <span className="eyebrow">Bible de conception</span>
            <h2>{group.title}</h2>
            <p>{group.description}</p>
          </div>
          <Icon />
        </div>
        <div className="blueprint-fields">
          {group.fields.map((field) => (
            <label key={field.key}>
              {field.label}
              <small>{field.prompt}</small>
              <textarea
                value={String(values[field.key] || "")}
                onChange={(e) =>
                  setValues({ ...values, [field.key]: e.target.value })
                }
                disabled={!canGroup}
              />
            </label>
          ))}
        </div>
        <button className="button full" disabled={!canGroup}>
          <Save /> Enregistrer cette section
        </button>
      </form>
    </div>
  );
}

function AgileTaskBoard({
  project,
  tasks,
  sprints,
  userId,
  canManage,
  onNotice,
}: {
  project: GameProject;
  tasks: StudioTask[];
  sprints: StudioSprint[];
  userId: string;
  canManage: boolean;
  onNotice: (s: string) => void;
}) {
  type View = "board" | "backlog" | "sprints";
  const statuses = Object.keys(taskStatusLabels) as StudioTaskStatus[];
  const blankTask = {
    title: "",
    description: "",
    acceptanceCriteria: "",
    stageId: "brief" as ProductionStageId,
    status: "backlog" as StudioTaskStatus,
    priority: "normal" as StudioTaskPriority,
    type: "task" as StudioTaskType,
    assigneeId: "",
    dueDate: "",
    sprintId: "",
    parentTaskId: "",
    blockedBy: [] as string[],
    labelsText: "",
    storyPoints: 0,
  };
  const blankSprint = {
    name: "",
    goal: "",
    startDate: "",
    endDate: "",
    status: "planned" as StudioSprintStatus,
  };
  const [view, setView] = useState<View>("board");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [sprintFilter, setSprintFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState(blankTask);
  const [sprintDraft, setSprintDraft] = useState(blankSprint);
  const [selectedId, setSelectedId] = useState("");
  const [comment, setComment] = useState("");
  const [, setTaskRevision] = useState(0);
  const selected = tasks.find((task) => task.id === selectedId);
  const activeSprint = sprints.find((sprint) => sprint.status === "active");
  const filtered = tasks.filter((task) => {
    const haystack =
      `${task.key || ""} ${task.title} ${task.description} ${(task.labels || []).join(" ")}`.toLowerCase();
    return (
      (!search || haystack.includes(search.toLowerCase())) &&
      (typeFilter === "all" || (task.type || "task") === typeFilter) &&
      (assigneeFilter === "all" || task.assigneeId === assigneeFilter) &&
      (sprintFilter === "all" ||
        (sprintFilter === "none"
          ? !task.sprintId
          : task.sprintId === sprintFilter))
    );
  });

  async function saveTask(e: FormEvent) {
    e.preventDefault();
    try {
      await call("saveStudioTask", { projectId: project.id, ...draft });
      setDraft(blankTask);
      setShowCreate(false);
      onNotice("Ticket créé.");
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Création impossible.");
    }
  }

  async function updateTask(e: FormEvent) {
    e.preventDefault();
    if (!selected) return;
    try {
      await call("saveStudioTask", {
        projectId: project.id,
        taskId: selected.id,
        title: selected.title,
        description: selected.description,
        acceptanceCriteria: selected.acceptanceCriteria || "",
        stageId: selected.stageId,
        status: selected.status,
        priority: selected.priority,
        type: selected.type || "task",
        assigneeId: selected.assigneeId || "",
        dueDate: selected.dueDate || "",
        sprintId: selected.sprintId || "",
        parentTaskId: selected.parentTaskId || "",
        blockedBy: selected.blockedBy || [],
        labels: selected.labels || [],
        storyPoints: selected.storyPoints || 0,
      });
      onNotice("Ticket enregistré.");
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Modification impossible.",
      );
    }
  }

  async function move(task: StudioTask, status: StudioTaskStatus) {
    try {
      await call("setStudioTaskStatus", {
        projectId: project.id,
        taskId: task.id,
        status,
      });
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Déplacement impossible.",
      );
    }
  }

  async function addComment(e: FormEvent) {
    e.preventDefault();
    if (!selected || !comment.trim()) return;
    try {
      await call("addStudioTaskComment", {
        projectId: project.id,
        taskId: selected.id,
        body: comment,
        mentionedUserIds: mentionedMembers(comment, project),
      });
      setComment("");
      onNotice("Commentaire ajouté.");
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Commentaire impossible.",
      );
    }
  }

  async function saveSprint(e: FormEvent) {
    e.preventDefault();
    try {
      await call("saveStudioSprint", { projectId: project.id, ...sprintDraft });
      setSprintDraft(blankSprint);
      onNotice("Sprint enregistré.");
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Sprint impossible.");
    }
  }

  function patchSelected(values: Partial<StudioTask>) {
    if (!selected) return;
    Object.assign(selected, values);
    setTaskRevision((value) => value + 1);
  }

  return (
    <div className="agile-shell">
      <header className="agile-header">
        <div>
          <span className="eyebrow">Suivi agile intégré</span>
          <h2>Planification et tickets</h2>
          <p>
            {activeSprint
              ? `${activeSprint.name} · ${activeSprint.goal}`
              : "Aucun sprint actif"}
          </p>
        </div>
        <button
          className="button"
          disabled={!canManage}
          onClick={() => setShowCreate(true)}
        >
          <Plus /> Créer un ticket
        </button>
      </header>

      <div className="agile-view-tabs">
        <button
          className={view === "board" ? "active" : ""}
          onClick={() => setView("board")}
        >
          <LayoutGrid /> Tableau
        </button>
        <button
          className={view === "backlog" ? "active" : ""}
          onClick={() => setView("backlog")}
        >
          <ListChecks /> Backlog
        </button>
        <button
          className={view === "sprints" ? "active" : ""}
          onClick={() => setView("sprints")}
        >
          <Layers3 /> Sprints
        </button>
      </div>

      {view !== "sprints" && (
        <div className="agile-filters">
          <label className="agile-search">
            <Search />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un ticket…"
            />
          </label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">Tous les types</option>
            {Object.entries(taskTypeLabels).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
          >
            <option value="all">Tous les responsables</option>
            <option value="">Non assignés</option>
            {project.members.map((member) => (
              <option value={member.uid} key={member.uid}>
                {member.displayName || member.email}
              </option>
            ))}
          </select>
          <select
            value={sprintFilter}
            onChange={(e) => setSprintFilter(e.target.value)}
          >
            <option value="all">Tous les sprints</option>
            <option value="none">Sans sprint</option>
            {sprints.map((sprint) => (
              <option value={sprint.id} key={sprint.id}>
                {sprint.name}
              </option>
            ))}
          </select>
          <span>{filtered.length} ticket(s)</span>
        </div>
      )}

      {view === "board" && (
        <div className="kanban-board">
          {statuses.map((status) => {
            const columnTasks = filtered.filter(
              (task) => task.status === status,
            );
            return (
              <section
                className={`kanban-column status-${status}`}
                key={status}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const task = tasks.find(
                    (item) =>
                      item.id === e.dataTransfer.getData("text/task-id"),
                  );
                  if (task && (canManage || task.assigneeId === userId))
                    move(task, status);
                }}
              >
                <header>
                  <strong>{taskStatusLabels[status]}</strong>
                  <span>{columnTasks.length}</span>
                </header>
                <div>
                  {columnTasks.map((task) => (
                    <TicketCard
                      key={task.id}
                      task={task}
                      project={project}
                      sprint={sprints.find((s) => s.id === task.sprintId)}
                      onOpen={() => setSelectedId(task.id)}
                    />
                  ))}
                  {columnTasks.length === 0 && (
                    <p className="kanban-empty">Déposez un ticket ici</p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {view === "backlog" && (
        <div className="backlog-list">
          <div className="backlog-heading">
            <span>Clé</span>
            <span>Ticket</span>
            <span>Responsable</span>
            <span>Sprint</span>
            <span>État</span>
            <span>Points</span>
          </div>
          {filtered.map((task) => (
            <button key={task.id} onClick={() => setSelectedId(task.id)}>
              <strong>
                {task.key || `T-${task.id.slice(0, 4).toUpperCase()}`}
              </strong>
              <span>
                <b>{taskTypeLabels[task.type || "task"]}</b>
                {task.title}
              </span>
              <span>
                {project.members.find((m) => m.uid === task.assigneeId)
                  ?.displayName || "—"}
              </span>
              <span>
                {sprints.find((s) => s.id === task.sprintId)?.name || "Backlog"}
              </span>
              <span>{taskStatusLabels[task.status]}</span>
              <span>{task.storyPoints || "—"}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="studio-empty">
              <ListChecks />
              <h3>Aucun ticket dans cette vue</h3>
            </div>
          )}
        </div>
      )}

      {view === "sprints" && (
        <div className="sprint-layout">
          <section className="sprint-list">
            {sprints.map((sprint) => {
              const sprintTasks = tasks.filter(
                (task) => task.sprintId === sprint.id,
              );
              const done = sprintTasks.filter(
                (task) => task.status === "done",
              ).length;
              const points = sprintTasks.reduce(
                (sum, task) => sum + (task.storyPoints || 0),
                0,
              );
              return (
                <article
                  className={`sprint-card ${sprint.status}`}
                  key={sprint.id}
                >
                  <div>
                    <span className={`sprint-status ${sprint.status}`}>
                      {sprintStatusLabels[sprint.status]}
                    </span>
                    <h3>{sprint.name}</h3>
                    <p>{sprint.goal}</p>
                  </div>
                  <div className="sprint-dates">
                    <CalendarDays /> {sprint.startDate} → {sprint.endDate}
                  </div>
                  <div className="progress">
                    <span
                      style={{
                        width: `${sprintTasks.length ? (done / sprintTasks.length) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <small>
                    {done}/{sprintTasks.length} tickets · {points} points
                  </small>
                </article>
              );
            })}
            {sprints.length === 0 && (
              <div className="studio-empty">
                <Layers3 />
                <h3>Aucun sprint</h3>
              </div>
            )}
          </section>
          <form
            className="panel project-form sprint-form"
            onSubmit={saveSprint}
          >
            <h2>
              <Plus /> Nouveau sprint
            </h2>
            <label>
              Nom
              <input
                value={sprintDraft.name}
                onChange={(e) =>
                  setSprintDraft({ ...sprintDraft, name: e.target.value })
                }
                required
                disabled={!canManage}
              />
            </label>
            <label>
              Objectif
              <textarea
                value={sprintDraft.goal}
                onChange={(e) =>
                  setSprintDraft({ ...sprintDraft, goal: e.target.value })
                }
                disabled={!canManage}
              />
            </label>
            <div className="two-columns">
              <label>
                Début
                <input
                  type="date"
                  value={sprintDraft.startDate}
                  onChange={(e) =>
                    setSprintDraft({
                      ...sprintDraft,
                      startDate: e.target.value,
                    })
                  }
                  required
                />
              </label>
              <label>
                Fin
                <input
                  type="date"
                  value={sprintDraft.endDate}
                  onChange={(e) =>
                    setSprintDraft({ ...sprintDraft, endDate: e.target.value })
                  }
                  required
                />
              </label>
            </div>
            <label>
              Statut
              <select
                value={sprintDraft.status}
                onChange={(e) =>
                  setSprintDraft({
                    ...sprintDraft,
                    status: e.target.value as StudioSprintStatus,
                  })
                }
              >
                {Object.entries(sprintStatusLabels).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <button className="button full" disabled={!canManage}>
              Créer le sprint
            </button>
          </form>
        </div>
      )}

      {showCreate && (
        <div className="ticket-modal" role="dialog" aria-modal="true">
          <form className="ticket-editor panel" onSubmit={saveTask}>
            <header>
              <div>
                <span className="eyebrow">Nouveau ticket</span>
                <h2>Ajouter au projet</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setShowCreate(false)}
              >
                <X />
              </button>
            </header>
            <TicketFields
              draft={draft}
              setDraft={setDraft}
              project={project}
              tasks={tasks}
              sprints={sprints}
              disabled={!canManage}
            />
            <button className="button full" disabled={!canManage}>
              <Plus /> Créer le ticket
            </button>
          </form>
        </div>
      )}

      {selected && (
        <div className="ticket-modal" role="dialog" aria-modal="true">
          <form
            className="ticket-editor ticket-detail panel"
            onSubmit={updateTask}
          >
            <header>
              <div>
                <span className="ticket-key">
                  {selected.key || `T-${selected.id.slice(0, 4).toUpperCase()}`}
                </span>
                <h2>{selected.title}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setSelectedId("")}
              >
                <X />
              </button>
            </header>
            <div className="ticket-detail-grid">
              <div className="ticket-main">
                <label>
                  Titre
                  <input
                    value={selected.title}
                    disabled={!canManage}
                    onChange={(e) => patchSelected({ title: e.target.value })}
                  />
                </label>
                <label>
                  Description
                  <textarea
                    value={selected.description}
                    disabled={!canManage}
                    onChange={(e) =>
                      patchSelected({ description: e.target.value })
                    }
                  />
                </label>
                <label>
                  Critères d’acceptation
                  <textarea
                    value={selected.acceptanceCriteria || ""}
                    disabled={!canManage}
                    onChange={(e) =>
                      patchSelected({ acceptanceCriteria: e.target.value })
                    }
                    placeholder="Conditions précises pour considérer le ticket terminé…"
                  />
                </label>
                <section className="ticket-comments">
                  <h3>
                    <MessageSquare /> Commentaires (
                    {selected.comments?.length || 0})
                  </h3>
                  {(selected.comments || []).map((item) => (
                    <article key={item.id}>
                      <strong>{item.authorName}</strong>
                      <p>{item.body}</p>
                    </article>
                  ))}
                  <div className="comment-composer">
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Ajouter un commentaire…"
                    />
                    <button
                      type="button"
                      className="ghost"
                      onClick={(e) => addComment(e as unknown as FormEvent)}
                    >
                      Envoyer
                    </button>
                  </div>
                </section>
              </div>
              <aside className="ticket-sidebar">
                <label>
                  État
                  <select
                    value={selected.status}
                    disabled={!canManage && selected.assigneeId !== userId}
                    onChange={(e) =>
                      move(selected, e.target.value as StudioTaskStatus)
                    }
                  >
                    {Object.entries(taskStatusLabels).map(([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Type
                  <select
                    value={selected.type || "task"}
                    disabled={!canManage}
                    onChange={(e) =>
                      patchSelected({ type: e.target.value as StudioTaskType })
                    }
                  >
                    {Object.entries(taskTypeLabels).map(([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Priorité
                  <select
                    value={selected.priority}
                    disabled={!canManage}
                    onChange={(e) =>
                      patchSelected({
                        priority: e.target.value as StudioTaskPriority,
                      })
                    }
                  >
                    {Object.entries(taskPriorityLabels).map(
                      ([value, label]) => (
                        <option value={value} key={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </select>
                </label>
                <label>
                  Responsable
                  <select
                    value={selected.assigneeId || ""}
                    disabled={!canManage}
                    onChange={(e) =>
                      patchSelected({ assigneeId: e.target.value })
                    }
                  >
                    <option value="">Non assigné</option>
                    {project.members.map((member) => (
                      <option value={member.uid} key={member.uid}>
                        {member.displayName || member.email}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Sprint
                  <select
                    value={selected.sprintId || ""}
                    disabled={!canManage}
                    onChange={(e) =>
                      patchSelected({ sprintId: e.target.value })
                    }
                  >
                    <option value="">Backlog</option>
                    {sprints.map((sprint) => (
                      <option value={sprint.id} key={sprint.id}>
                        {sprint.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Points
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={selected.storyPoints || 0}
                    disabled={!canManage}
                    onChange={(e) =>
                      patchSelected({ storyPoints: Number(e.target.value) })
                    }
                  />
                </label>
                <label>
                  Échéance
                  <input
                    type="date"
                    value={selected.dueDate || ""}
                    disabled={!canManage}
                    onChange={(e) => patchSelected({ dueDate: e.target.value })}
                  />
                </label>
                <label>
                  Étiquettes
                  <input
                    value={(selected.labels || []).join(", ")}
                    disabled={!canManage}
                    onChange={(e) =>
                      patchSelected({
                        labels: e.target.value
                          .split(",")
                          .map((v) => v.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </label>
                <label>
                  Ticket parent
                  <select
                    value={selected.parentTaskId || ""}
                    disabled={!canManage}
                    onChange={(e) =>
                      patchSelected({ parentTaskId: e.target.value })
                    }
                  >
                    <option value="">Aucun</option>
                    {tasks
                      .filter((task) => task.id !== selected.id)
                      .map((task) => (
                        <option value={task.id} key={task.id}>
                          {task.key || "Ticket"} · {task.title}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  Bloqué par
                  <select
                    value={selected.blockedBy?.[0] || ""}
                    disabled={!canManage}
                    onChange={(e) =>
                      patchSelected({
                        blockedBy: e.target.value ? [e.target.value] : [],
                      })
                    }
                  >
                    <option value="">Aucun blocage</option>
                    {tasks
                      .filter((task) => task.id !== selected.id)
                      .map((task) => (
                        <option value={task.id} key={task.id}>
                          {task.key || "Ticket"} · {task.title}
                        </option>
                      ))}
                  </select>
                </label>
                {canManage && (
                  <button className="button full">
                    <Save /> Enregistrer
                  </button>
                )}
              </aside>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function TicketCard({
  task,
  project,
  sprint,
  onOpen,
}: {
  task: StudioTask;
  project: GameProject;
  sprint?: StudioSprint;
  onOpen: () => void;
}) {
  const blocked = Boolean(task.blockedBy?.length);
  return (
    <article
      className={`jira-card priority-${task.priority} ${blocked ? "blocked" : ""}`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/task-id", task.id)}
      onClick={onOpen}
    >
      <div className="jira-card-top">
        <span className="ticket-key">
          {task.key || `T-${task.id.slice(0, 4).toUpperCase()}`}
        </span>
        <span className={`task-priority ${task.priority}`}>
          {taskPriorityLabels[task.priority]}
        </span>
      </div>
      <h3>{task.title}</h3>
      <div className="jira-labels">
        {(task.labels || []).slice(0, 3).map((label) => (
          <span key={label}>
            <Tag />
            {label}
          </span>
        ))}
      </div>
      {blocked && (
        <small className="blocked-label">
          <Link2 /> Bloqué
        </small>
      )}
      <div className="jira-card-bottom">
        <span>{taskTypeLabels[task.type || "task"]}</span>
        {sprint && <span>{sprint.name}</span>}
        <b>{task.storyPoints || "—"}</b>
        <span className="assignee-avatar">
          {(
            project.members.find((m) => m.uid === task.assigneeId)
              ?.displayName || "?"
          )
            .slice(0, 1)
            .toUpperCase()}
        </span>
      </div>
    </article>
  );
}

function TicketFields({
  draft,
  setDraft,
  project,
  tasks,
  sprints,
  disabled,
}: {
  draft: ReturnType<typeof makeTaskDraft>;
  setDraft: (value: ReturnType<typeof makeTaskDraft>) => void;
  project: GameProject;
  tasks: StudioTask[];
  sprints: StudioSprint[];
  disabled: boolean;
}) {
  return (
    <div className="ticket-fields">
      <label>
        Titre
        <input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          required
          disabled={disabled}
        />
      </label>
      <div className="two-columns">
        <label>
          Type
          <select
            value={draft.type}
            onChange={(e) =>
              setDraft({ ...draft, type: e.target.value as StudioTaskType })
            }
            disabled={disabled}
          >
            {Object.entries(taskTypeLabels).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Priorité
          <select
            value={draft.priority}
            onChange={(e) =>
              setDraft({
                ...draft,
                priority: e.target.value as StudioTaskPriority,
              })
            }
            disabled={disabled}
          >
            {Object.entries(taskPriorityLabels).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        Description
        <textarea
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          disabled={disabled}
        />
      </label>
      <label>
        Critères d’acceptation
        <textarea
          value={draft.acceptanceCriteria}
          onChange={(e) =>
            setDraft({ ...draft, acceptanceCriteria: e.target.value })
          }
          disabled={disabled}
        />
      </label>
      <div className="two-columns">
        <label>
          Phase
          <select
            value={draft.stageId}
            onChange={(e) =>
              setDraft({
                ...draft,
                stageId: e.target.value as ProductionStageId,
              })
            }
            disabled={disabled}
          >
            {stages.map((stage) => (
              <option value={stage.id} key={stage.id}>
                {stage.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Statut
          <select
            value={draft.status}
            onChange={(e) =>
              setDraft({ ...draft, status: e.target.value as StudioTaskStatus })
            }
            disabled={disabled}
          >
            {Object.entries(taskStatusLabels).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="two-columns">
        <label>
          Responsable
          <select
            value={draft.assigneeId}
            onChange={(e) => setDraft({ ...draft, assigneeId: e.target.value })}
            disabled={disabled}
          >
            <option value="">Non assigné</option>
            {project.members.map((member) => (
              <option value={member.uid} key={member.uid}>
                {member.displayName || member.email}
              </option>
            ))}
          </select>
        </label>
        <label>
          Sprint
          <select
            value={draft.sprintId}
            onChange={(e) => setDraft({ ...draft, sprintId: e.target.value })}
            disabled={disabled}
          >
            <option value="">Backlog</option>
            {sprints.map((sprint) => (
              <option value={sprint.id} key={sprint.id}>
                {sprint.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="two-columns">
        <label>
          Points
          <input
            type="number"
            min="0"
            max="100"
            value={draft.storyPoints}
            onChange={(e) =>
              setDraft({ ...draft, storyPoints: Number(e.target.value) })
            }
            disabled={disabled}
          />
        </label>
        <label>
          Échéance
          <input
            type="date"
            value={draft.dueDate}
            onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
            disabled={disabled}
          />
        </label>
      </div>
      <label>
        Étiquettes
        <input
          value={draft.labelsText}
          onChange={(e) => setDraft({ ...draft, labelsText: e.target.value })}
          placeholder="interface, mobile, urgent"
          disabled={disabled}
        />
      </label>
      <div className="two-columns">
        <label>
          Ticket parent
          <select
            value={draft.parentTaskId}
            onChange={(e) =>
              setDraft({ ...draft, parentTaskId: e.target.value })
            }
            disabled={disabled}
          >
            <option value="">Aucun</option>
            {tasks.map((task) => (
              <option value={task.id} key={task.id}>
                {task.key || "Ticket"} · {task.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          Bloqué par
          <select
            value={draft.blockedBy[0] || ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                blockedBy: e.target.value ? [e.target.value] : [],
              })
            }
            disabled={disabled}
          >
            <option value="">Aucun blocage</option>
            {tasks.map((task) => (
              <option value={task.id} key={task.id}>
                {task.key || "Ticket"} · {task.title}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

function makeTaskDraft() {
  return {
    title: "",
    description: "",
    acceptanceCriteria: "",
    stageId: "brief" as ProductionStageId,
    status: "backlog" as StudioTaskStatus,
    priority: "normal" as StudioTaskPriority,
    type: "task" as StudioTaskType,
    assigneeId: "",
    dueDate: "",
    sprintId: "",
    parentTaskId: "",
    blockedBy: [] as string[],
    labelsText: "",
    storyPoints: 0,
  };
}

function TaskBoard(props: Parameters<typeof AgileTaskBoard>[0]) {
  return <AgileTaskBoard {...props} />;
}

function LegacyTaskBoard({
  project,
  tasks,
  userId,
  canManage,
  onNotice,
}: {
  project: GameProject;
  tasks: StudioTask[];
  userId: string;
  canManage: boolean;
  onNotice: (s: string) => void;
}) {
  const empty = {
    title: "",
    description: "",
    stageId: "brief" as ProductionStageId,
    status: "todo" as StudioTaskStatus,
    priority: "normal" as StudioTaskPriority,
    assigneeId: "",
    dueDate: "",
  };
  const [draft, setDraft] = useState(empty);
  async function save(e: FormEvent) {
    e.preventDefault();
    try {
      await call("saveStudioTask", { projectId: project.id, ...draft });
      setDraft(empty);
      onNotice("Tâche créée.");
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Création impossible.");
    }
  }
  async function move(task: StudioTask, status: StudioTaskStatus) {
    try {
      await call("setStudioTaskStatus", {
        projectId: project.id,
        taskId: task.id,
        status,
      });
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Modification impossible.",
      );
    }
  }
  return (
    <div className="task-shell">
      <section>
        <div className="studio-section-title">
          <div>
            <span className="eyebrow">Production</span>
            <h2>Plan de travail</h2>
          </div>
        </div>
        <div className="task-list">
          {tasks.map((task) => (
            <article className="task-card" key={task.id}>
              <div className="task-card-top">
                <span className={`task-priority ${task.priority}`}>
                  {taskPriorityLabels[task.priority]}
                </span>
                <span>{stages.find((s) => s.id === task.stageId)?.label}</span>
              </div>
              <h3>{task.title}</h3>
              <p>{task.description}</p>
              <div className="task-meta">
                <span>
                  <Users />
                  {project.members.find((m) => m.uid === task.assigneeId)
                    ?.displayName || "Non assignée"}
                </span>
                {task.dueDate && (
                  <span>
                    <Clock3 />
                    {task.dueDate}
                  </span>
                )}
              </div>
              <label>
                Statut
                <select
                  value={task.status}
                  disabled={!canManage && task.assigneeId !== userId}
                  onChange={(e) =>
                    move(task, e.target.value as StudioTaskStatus)
                  }
                >
                  {Object.entries(taskStatusLabels).map(([v, l]) => (
                    <option value={v} key={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
            </article>
          ))}
          {tasks.length === 0 && (
            <div className="studio-empty">
              <ListChecks />
              <h2>Aucune tâche</h2>
            </div>
          )}
        </div>
      </section>
      <form className="panel project-form" onSubmit={save}>
        <h2>
          <Plus /> Nouvelle tâche
        </h2>
        <label>
          Titre
          <input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            required
            disabled={!canManage}
          />
        </label>
        <label>
          Description
          <textarea
            value={draft.description}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value })
            }
            disabled={!canManage}
          />
        </label>
        <label>
          Phase
          <select
            value={draft.stageId}
            onChange={(e) =>
              setDraft({
                ...draft,
                stageId: e.target.value as ProductionStageId,
              })
            }
            disabled={!canManage}
          >
            {stages.map((s) => (
              <option value={s.id} key={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <div className="two-columns">
          <label>
            Priorité
            <select
              value={draft.priority}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  priority: e.target.value as StudioTaskPriority,
                })
              }
            >
              {Object.entries(taskPriorityLabels).map(([v, l]) => (
                <option value={v} key={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label>
            Échéance
            <input
              type="date"
              value={draft.dueDate}
              onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
            />
          </label>
        </div>
        <label>
          Responsable
          <select
            value={draft.assigneeId}
            onChange={(e) => setDraft({ ...draft, assigneeId: e.target.value })}
          >
            <option value="">Non assignée</option>
            {project.members.map((m) => (
              <option value={m.uid} key={m.uid}>
                {m.displayName || m.email}
              </option>
            ))}
          </select>
        </label>
        <button className="button full" disabled={!canManage}>
          Créer la tâche
        </button>
      </form>
    </div>
  );
}

function ContentEditor({
  projectId,
  puzzles,
  canEdit,
  onNotice,
}: {
  projectId: string;
  puzzles: DraftPuzzle[];
  canEdit: boolean;
  onNotice: (s: string) => void;
}) {
  const empty: DraftPuzzle = {
    id: "",
    order: puzzles.length,
    title: "",
    story: "",
    question: "",
    answer: "",
    hints: [],
    latitude: null,
    longitude: null,
    radiusMeters: 100,
    mode: "shared",
    fragments: [],
    minPlayers: 2,
  };
  const [draft, setDraft] = useState<DraftPuzzle>(empty);
  async function save(e: FormEvent) {
    e.preventDefault();
    try {
      await call("saveDraftPuzzle", {
        projectId,
        puzzleId: draft.id || undefined,
        ...draft,
        hintsText: draft.hints.join("\n"),
      });
      onNotice("Énigme enregistrée.");
      setDraft({ ...empty, order: puzzles.length + 1 });
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Enregistrement impossible.",
      );
    }
  }
  return (
    <div className="studio-layout">
      <section>
        <h2>Énigmes du brouillon</h2>
        <div className="draft-list">
          {puzzles.map((p, i) => (
            <button key={p.id} onClick={() => setDraft(p)}>
              <span>{i + 1}</span>
              <div>
                <strong>{p.title}</strong>
                <small>{p.question}</small>
              </div>
              <FilePenLine />
            </button>
          ))}
        </div>
      </section>
      <form className="panel project-form" onSubmit={save}>
        <h2>
          <Sparkles /> {draft.id ? "Modifier" : "Nouvelle énigme"}
        </h2>
        <label>
          Ordre
          <input
            type="number"
            value={draft.order}
            onChange={(e) =>
              setDraft({ ...draft, order: Number(e.target.value) })
            }
          />
        </label>
        <label>
          Titre
          <input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            required
          />
        </label>
        <label>
          Mise en situation
          <textarea
            value={draft.story}
            onChange={(e) => setDraft({ ...draft, story: e.target.value })}
            required
          />
        </label>
        <label>
          Question ou défi commun
          <textarea
            value={draft.question}
            onChange={(e) => setDraft({ ...draft, question: e.target.value })}
            required
          />
        </label>
        <label>Type d’énigme
          <select value={draft.mode || "shared"} onChange={e => setDraft({ ...draft, mode: e.target.value as "shared" | "cooperative" })}>
            <option value="shared">Énigme commune</option><option value="cooperative">Morceaux privés à réunir</option>
          </select>
        </label>
        {draft.mode === "cooperative" && <fieldset>
          <legend>Morceaux privés</legend>
          <p>Créez 2 à 20 morceaux complémentaires distincts. Tous seront répartis entre les joueurs présents, sans doublon (2 à 5 joueurs, au maximum autant que de morceaux). Chacun recevra au moins un morceau. Le chef lance la distribution une fois l’équipe réunie.</p>
          <label>Minimum de joueurs<input type="number" min={2} max={5} value={draft.minPlayers || 2} onChange={e => setDraft({ ...draft, minPlayers: Number(e.target.value) })}/></label>
          {(draft.fragments || []).map((fragment, i) => <label key={i}>Morceau {i + 1}
            <textarea required maxLength={5000} value={fragment} onChange={e => setDraft({ ...draft, fragments: (draft.fragments || []).map((v, j) => j === i ? e.target.value : v) })}/>
            <button type="button" className="ghost" onClick={() => setDraft({ ...draft, fragments: (draft.fragments || []).filter((_, j) => j !== i) })}>Retirer ce morceau</button>
          </label>)}
          <button type="button" className="ghost" disabled={(draft.fragments || []).length >= 20} onClick={() => setDraft({ ...draft, fragments: [...(draft.fragments || []), ""] })}>Ajouter un morceau</button>
        </fieldset>}
        <label>
          Réponse
          <input
            value={draft.answer}
            onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
            required
          />
        </label>
        <label>
          Indices (un par ligne)
          <textarea
            value={draft.hints.join("\n")}
            onChange={(e) =>
              setDraft({ ...draft, hints: e.target.value.split("\n") })
            }
          />
        </label>
        <div className="coordinate-grid">
          <label>
            Latitude
            <input
              type="number"
              step="any"
              value={draft.latitude ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  latitude: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          </label>
          <label>
            Longitude
            <input
              type="number"
              step="any"
              value={draft.longitude ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  longitude: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          </label>
          <label>
            Rayon
            <input
              type="number"
              value={draft.radiusMeters ?? 100}
              onChange={(e) =>
                setDraft({ ...draft, radiusMeters: Number(e.target.value) })
              }
            />
          </label>
        </div>
        <button className="button full" disabled={!canEdit}>
          <Save /> Enregistrer
        </button>
      </form>
    </div>
  );
}

function QualityCenter({
  project,
  issues,
  puzzles,
  canReport,
  onNotice,
}: {
  project: GameProject;
  issues: StudioIssue[];
  puzzles: DraftPuzzle[];
  canReport: boolean;
  onNotice: (s: string) => void;
}) {
  const empty = {
    title: "",
    steps: "",
    expected: "",
    actual: "",
    environment: "Téléphone Android · Chrome",
    severity: "major" as StudioIssueSeverity,
    status: "open" as StudioIssueStatus,
    puzzleId: "",
    assigneeId: "",
  };
  const [draft, setDraft] = useState(empty);
  async function save(e: FormEvent) {
    e.preventDefault();
    try {
      await call("saveStudioIssue", { projectId: project.id, ...draft });
      setDraft(empty);
      onNotice("Anomalie ajoutée.");
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Signalement impossible.",
      );
    }
  }
  async function update(issue: StudioIssue, status: StudioIssueStatus) {
    await call("saveStudioIssue", { projectId: project.id, ...issue, status });
  }
  return (
    <div className="qa-shell">
      <section>
        <h2>Registre des anomalies</h2>
        <div className="issue-list">
          {issues.map((issue) => (
            <article
              className={`issue-card severity-${issue.severity}`}
              key={issue.id}
            >
              <div>
                <span className={`issue-severity ${issue.severity}`}>
                  {issueSeverityLabels[issue.severity]}
                </span>
                <span>{issue.environment}</span>
              </div>
              <h3>{issue.title}</h3>
              <p>{issue.actual}</p>
              <label>
                Statut
                <select
                  value={issue.status}
                  disabled={!canReport}
                  onChange={(e) =>
                    update(issue, e.target.value as StudioIssueStatus)
                  }
                >
                  {Object.entries(issueStatusLabels).map(([v, l]) => (
                    <option value={v} key={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
            </article>
          ))}
        </div>
      </section>
      <form className="panel project-form" onSubmit={save}>
        <h2>
          <Bug /> Signaler une anomalie
        </h2>
        <label>
          Titre
          <input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            required
          />
        </label>
        <div className="two-columns">
          <label>
            Gravité
            <select
              value={draft.severity}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  severity: e.target.value as StudioIssueSeverity,
                })
              }
            >
              {Object.entries(issueSeverityLabels).map(([v, l]) => (
                <option value={v} key={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label>
            Énigme
            <select
              value={draft.puzzleId}
              onChange={(e) => setDraft({ ...draft, puzzleId: e.target.value })}
            >
              <option value="">Général</option>
              {puzzles.map((p) => (
                <option value={p.id} key={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label>
          Environnement
          <input
            value={draft.environment}
            onChange={(e) =>
              setDraft({ ...draft, environment: e.target.value })
            }
          />
        </label>
        <label>
          Étapes pour reproduire
          <textarea
            value={draft.steps}
            onChange={(e) => setDraft({ ...draft, steps: e.target.value })}
          />
        </label>
        <label>
          Résultat attendu
          <textarea
            value={draft.expected}
            onChange={(e) => setDraft({ ...draft, expected: e.target.value })}
          />
        </label>
        <label>
          Résultat constaté
          <textarea
            value={draft.actual}
            onChange={(e) => setDraft({ ...draft, actual: e.target.value })}
          />
        </label>
        <button className="button full" disabled={!canReport}>
          Enregistrer
        </button>
      </form>
    </div>
  );
}

function TeamAccess({
  project,
  canManage,
  onNotice,
}: {
  project: GameProject;
  canManage: boolean;
  onNotice: (s: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<StudioRole>("writer");
  async function assign(e: FormEvent) {
    e.preventDefault();
    try {
      await call("assignGameProjectMember", {
        projectId: project.id,
        email,
        role,
      });
      setEmail("");
      onNotice("Membre ajouté.");
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Ajout impossible.");
    }
  }
  return (
    <div className="team-access">
      <section>
        <h2>Équipe du projet</h2>
        <div className="member-list">
          <article className="member-card admin-member">
            <ShieldCheck />
            <div>
              <strong>Administrateur</strong>
              <span>Tous les accès</span>
            </div>
          </article>
          {project.members.map((member) => (
            <article className="member-card" key={member.uid}>
              <Users />
              <div>
                <strong>{member.displayName || member.email}</strong>
                <span>{roles[member.role]?.label}</span>
                <small>
                  {member.permissions
                    .map((p) => permissionLabels[p])
                    .join(" · ")}
                </small>
              </div>
            </article>
          ))}
        </div>
      </section>
      <aside>
        <form className="panel project-form" onSubmit={assign}>
          <h2>
            <Plus /> Ajouter un membre
          </h2>
          <label>
            Courriel
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            Rôle
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as StudioRole)}
            >
              {Object.entries(roles).map(([key, value]) => (
                <option value={key} key={key}>
                  {value.label}
                </option>
              ))}
            </select>
          </label>
          <button className="button full" disabled={!canManage}>
            Attribuer les accès
          </button>
        </form>
      </aside>
    </div>
  );
}

function Sandbox({
  project,
  puzzles,
  allowed,
}: {
  project: GameProject;
  puzzles: DraftPuzzle[];
  allowed: boolean;
}) {
  const [index, setIndex] = useState(0);
  const puzzle = puzzles[index];
  const [playerCount, setPlayerCount] = useState(2);
  const [playerSlot, setPlayerSlot] = useState(0);
  useEffect(() => {
    if (puzzle?.mode === "cooperative") {
      setPlayerCount((count) => Math.max(count, puzzle.minPlayers || 2));
    }
  }, [puzzle?.id, puzzle?.mode, puzzle?.minPlayers]);
  const progress = useMemo(
    () => (puzzles.length ? ((index + 1) / puzzles.length) * 100 : 0),
    [index, puzzles.length],
  );
  if (!allowed)
    return (
      <div className="sandbox-locked">
        <ShieldCheck />
        <h2>Accès sandbox requis</h2>
      </div>
    );
  return (
    <div className="sandbox-shell">
      <div className="sandbox-toolbar">
        <span>
          <Beaker /> SANDBOX — invisible au public
        </span>
        <b>{project.title}</b>
      </div>
      {!puzzle ? (
        <div className="sandbox-locked">
          <Code2 />
          <h2>Ajoutez une énigme</h2>
        </div>
      ) : (
        <div className="sandbox-preview">
          <div className="progress">
            <span style={{ width: `${progress}%` }} />
          </div>
          <span className="eyebrow">
            Test · {index + 1}/{puzzles.length}
          </span>
          <h2>{puzzle.title}</h2>
          <p className="sandbox-story">{puzzle.story}</p>
          <div className="sandbox-question">
            <Sparkles />
            <p>{puzzle.question}</p>
          </div>
          {puzzle.mode === "cooperative" && <section className="panel">
            <h3>Tester la distribution privée</h3>
            <label>Joueurs présents<select value={playerCount} onChange={e => { setPlayerCount(Number(e.target.value)); setPlayerSlot(0); }}>{[2,3,4,5].map(n => <option key={n} value={n}>{n} joueurs</option>)}</select></label>
            <label>Point de vue<select value={playerSlot} onChange={e => setPlayerSlot(Number(e.target.value))}>{Array.from({ length: playerCount }, (_, i) => <option key={i} value={i}>Joueur {i + 1}</option>)}</select></label>
            {playerCount < (puzzle.minPlayers || 2) || playerCount > (puzzle.fragments || []).length ? <p>Nombre de joueurs incompatible avec cette énigme.</p> : (puzzle.fragments || []).filter((_, i) => i % playerCount === playerSlot).map((v, i) => <p style={{ whiteSpace: "pre-wrap" }} key={i}>{v}</p>)}
          </section>}
          {puzzle.hints.map((hint, i) => (
            <details key={i}>
              <summary>Indice {i + 1}</summary>
              <p>{hint}</p>
            </details>
          ))}
          <details className="sandbox-answer">
            <summary>Réponse de contrôle</summary>
            <strong>{puzzle.answer}</strong>
          </details>
          <div className="sandbox-nav">
            <button
              className="ghost"
              disabled={index === 0}
              onClick={() => setIndex((i) => i - 1)}
            >
              <ChevronLeft /> Précédente
            </button>
            <button
              className="button"
              disabled={index >= puzzles.length - 1}
              onClick={() => setIndex((i) => i + 1)}
            >
              Suivante <ChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReleaseCenter({
  project,
  issues,
  puzzles,
  canApprove,
  isAdmin,
  onPublish,
  onNotice,
}: {
  project: GameProject;
  issues: StudioIssue[];
  puzzles: DraftPuzzle[];
  canApprove: boolean;
  isAdmin: boolean;
  onPublish: () => void;
  onNotice: (s: string) => void;
}) {
  const [availableFrom, setAvailableFrom] = useState(project.availableFrom || "");
  const [availableUntil, setAvailableUntil] = useState(project.availableUntil || "");
  useEffect(() => {
    setAvailableFrom(project.availableFrom || "");
    setAvailableUntil(project.availableUntil || "");
  }, [project.availableFrom, project.availableUntil]);
  const done = releaseChecks.filter(
    ([id]) => project.releaseChecks?.[id],
  ).length;
  const blocking = issues.filter(
    (i) =>
      i.severity === "blocker" && !["resolved", "verified"].includes(i.status),
  );
  const stagesReady = stages
    .filter((s) => s.id !== "launch")
    .every((s) => project.completedStages?.includes(s.id));
  const ready =
    done === releaseChecks.length &&
    blocking.length === 0 &&
    puzzles.length > 0 &&
    stagesReady;
  async function toggle(checkId: string) {
    try {
      await call("setReleaseCheck", {
        projectId: project.id,
        checkId,
        checked: !project.releaseChecks?.[checkId],
      });
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Validation impossible.",
      );
    }
  }
  async function saveAvailability(e: FormEvent) {
    e.preventDefault();
    try {
      await call("updateGameProject", {
        projectId: project.id,
        availableFrom,
        availableUntil,
      });
      onNotice("Période de disponibilité enregistrée.");
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Enregistrement impossible.",
      );
    }
  }
  return (
    <div className="release-shell">
      <section className="release-summary">
        <div className={`release-score ${ready ? "ready" : ""}`}>
          <strong>
            {done}/{releaseChecks.length}
          </strong>
          <span>contrôles validés</span>
        </div>
        <div>
          <span className="eyebrow">Porte de publication</span>
          <h2>{ready ? "Le jeu est prêt" : "Validation finale requise"}</h2>
        </div>
      </section>
      <div className="release-grid">
        <form className="panel release-availability" onSubmit={saveAvailability}>
          <h2>
            <CalendarDays /> Disponibilité publique
          </h2>
          <p>
            Un billet donne accès au jeu jusqu’à sa réussite ou jusqu’à la date
            de fin ci-dessous. Laissez la date de fin vide pour une disponibilité
            permanente.
          </p>
          <div className="availability-fields">
            <label>
              Disponible à partir du
              <input
                type="date"
                value={availableFrom}
                onChange={(e) => setAvailableFrom(e.target.value)}
                disabled={!canApprove}
              />
            </label>
            <label>
              Disponible jusqu’au (inclusivement)
              <input
                type="date"
                value={availableUntil}
                min={availableFrom || undefined}
                onChange={(e) => setAvailableUntil(e.target.value)}
                disabled={!canApprove}
              />
            </label>
          </div>
          <button className="button full" disabled={!canApprove}>
            <Save /> Enregistrer la disponibilité
          </button>
        </form>
        <section className="panel release-checklist">
          <h2>
            <ClipboardCheck /> Checklist obligatoire
          </h2>
          {releaseChecks.map(([id, label]) => (
            <label
              className={project.releaseChecks?.[id] ? "checked" : ""}
              key={id}
            >
              <input
                type="checkbox"
                checked={Boolean(project.releaseChecks?.[id])}
                disabled={!canApprove}
                onChange={() => toggle(id)}
              />
              <span>
                <Check />
              </span>
              <strong>{label}</strong>
            </label>
          ))}
        </section>
        <aside className="panel release-gates">
          <h2>
            <Flag /> État des portes
          </h2>
          <ReleaseGate ok={stagesReady} label="Phases 1 à 7 terminées" />
          <ReleaseGate
            ok={puzzles.length > 0}
            label={`${puzzles.length} énigme(s)`}
          />
          <ReleaseGate
            ok={blocking.length === 0}
            label={
              blocking.length
                ? `${blocking.length} blocage(s) ouvert(s)`
                : "Aucun blocage ouvert"
            }
          />
          <ReleaseGate
            ok={done === releaseChecks.length}
            label="Checklist complète"
          />
          <button
            className="button full"
            disabled={!isAdmin || !ready}
            onClick={onPublish}
          >
            <Rocket /> Publier le jeu
          </button>
        </aside>
      </div>
    </div>
  );
}
function ReleaseGate({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={`release-gate ${ok ? "ok" : ""}`}>
      {ok ? <CheckCircle2 /> : <Circle />}
      <span>{label}</span>
    </div>
  );
}

